import React, { useState, useEffect, useRef } from 'react';
import { Triangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  generateShippingLabel,
  fetchVendorPickupAddresses,
  updateOrderPickupAddress,
  updateOrderShippingAddress,
  calculateShippingCost,
  normalizeShippingCostCalculatorResponse,
  removeShippingLabel,
  addManualShippingDetails,
  generateInvoices,
  getOrderInvoice,
  cancelInvoices,
  sendOrderFollowUp,
  setOrderException,
  revokeOrderException,
  applyOrderPenalty,
} from '../../api/api';
import { showToast } from '../../utils/toast';
import {
  PROZO_CHANNEL_NAMES,
  resolveProzoChannel,
} from '../../utils/prozoChannels';
import { maskIfHidden, maskAddressIfHidden } from '../../utils/customerMask';
import { getManifestState } from '../../utils/helpers';
import { vendorSyncDisplay } from './vendorSyncStatus';
import PrescriptionUploadModal, {
  openOrderPrescription,
  orderNeedsPrescription,
} from './PrescriptionUploadModal';
import '../ShipturtleProducts/ManageProducts.css';
import './ManageOrders.css';

const PROZO_CARRIER_NAME = 'PROZO';
const MANUAL_CHANNEL_VALUE = 'add_manually';

// ============================================================================
// REGEX PATTERNS FOR ADDRESS VALIDATION (Prozo-compatible)
// ============================================================================

// Indian PINCODE: 6 digits (100000-999999)
const PINCODE_REGEX = /^[1-9][0-9]{5}$/;

// Indian Phone: 10 digits (starts with 6-9)
const PHONE_REGEX = /^[6-9][0-9]{9}$/;

// Phone with country code: +91 followed by 10 digits
const PHONE_WITH_COUNTRY_REGEX = /^\+91[6-9][0-9]{9}$/;

// Email validation
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Address line validation: alphanumeric, spaces, commas, dots, hyphens, slashes
const ADDRESS_LINE_REGEX = /^[a-zA-Z0-9\s,.()/-]{3,100}$/;

// City name validation: letters, spaces, hyphens
const CITY_REGEX = /^[a-zA-Z][a-zA-Z\s-]{1,50}$/;

// State name validation: letters, spaces, hyphens
const STATE_REGEX = /^[a-zA-Z][a-zA-Z\s-]{1,50}$/;

// Weight validation: positive number (grams, max 50000g = 50kg for most couriers)
const WEIGHT_REGEX = /^[1-9][0-9]*$/;

// Dimension validation: positive number (cm, max 300cm)
const DIMENSION_REGEX = /^[1-9][0-9]{0,2}$/;

// ============================================================================
// ADDRESS VALIDATION HELPERS
// ============================================================================

/**
 * Do not show Ekart in shipping cost UI or use it for channel price hints.
 * Full quote list remains in state; filter applied only where we render or match parent names.
 */
function excludeDisabledShippingPartners(costs) {
  if (!Array.isArray(costs)) return [];
  return costs.filter((c) => {
    const parentName = (c.provider?.parent || '').toLowerCase();
    if (parentName.includes('ekart')) return false;
    return true;
  });
}

/**
 * Validates an address object according to Prozo requirements
 * Returns { isValid: boolean, errors: string[], warnings: string[] }
 */
const validateAddressForProzo = (address, addressType = 'shipping') => {
  const errors = [];
  const warnings = [];

  if (!address) {
    errors.push(`${addressType} address is missing`);
    return { isValid: false, errors, warnings };
  }

  // Validate address_line_1 (required)
  if (!address.address_line_1 || !address.address_line_1.trim()) {
    errors.push(`${addressType} Address Line 1 is required`);
  } else if (!ADDRESS_LINE_REGEX.test(address.address_line_1.trim())) {
    errors.push(`${addressType} Address Line 1 contains invalid characters (use letters, numbers, spaces, commas, dots, hyphens, parentheses only)`);
  } else if (address.address_line_1.trim().length < 5) {
    warnings.push(`${addressType} Address Line 1 is too short (minimum 5 characters recommended)`);
  }

  // Validate address_line_2 (optional but recommended)
  if (address.address_line_2 && address.address_line_2.trim()) {
    if (!ADDRESS_LINE_REGEX.test(address.address_line_2.trim())) {
      errors.push(`${addressType} Address Line 2 contains invalid characters (use letters, numbers, spaces, commas, dots, hyphens, parentheses only)`);
    }
  }

  // Validate city (required)
  if (!address.city || !address.city.trim()) {
    errors.push(`${addressType} City is required`);
  } else if (!CITY_REGEX.test(address.city.trim())) {
    errors.push(`${addressType} City name contains invalid characters`);
  }

  // Validate state (required)
  if (!address.state || !address.state.trim()) {
    errors.push(`${addressType} State is required`);
  } else if (!STATE_REGEX.test(address.state.trim())) {
    errors.push(`${addressType} State name contains invalid characters`);
  }

  // Validate pincode (required - critical for Prozo)
  if (!address.pincode || !address.pincode.trim()) {
    errors.push(`${addressType} Pincode is required`);
  } else {
    const pincodeClean = address.pincode.replace(/\s/g, '');
    if (!PINCODE_REGEX.test(pincodeClean)) {
      errors.push(`${addressType} Pincode must be a valid 6-digit Indian PIN code (100000-999999)`);
    } else {
      // Check if pincode starts with 0 (invalid for most serviceable areas)
      if (pincodeClean.startsWith('0')) {
        warnings.push(`${addressType} Pincode starts with 0 - may not be serviceable by all couriers`);
      }
    }
  }

  // Validate country (required)
  if (!address.country || !address.country.trim()) {
    errors.push(`${addressType} Country is required`);
  }

  // Validate phone (required - critical for delivery)
  if (!address.phone || !address.phone.trim()) {
    errors.push(`${addressType} Phone number is required`);
  } else {
    const phoneClean = address.phone.replace(/[\s-]/g, '');
    const isValidPhone = PHONE_REGEX.test(phoneClean) || PHONE_WITH_COUNTRY_REGEX.test(phoneClean);
    if (!isValidPhone) {
      errors.push(`${addressType} Phone must be a valid 10-digit Indian mobile number (starts with 6-9) or +91 followed by 10 digits`);
    }
  }

  // Validate landmark (optional)
  if (address.landmark && address.landmark.trim()) {
    if (address.landmark.trim().length < 3) {
      warnings.push(`${addressType} Landmark is too short (minimum 3 characters)`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Validates dimensions and weight for shipping
 */
const validatePackageDimensions = (length, breadth, height, weight) => {
  const errors = [];
  const warnings = [];

  // Check if all values are provided
  if (!length || length <= 0) errors.push('Length is required');
  if (!breadth || breadth <= 0) errors.push('Breadth is required');
  if (!height || height <= 0) errors.push('Height is required');
  if (!weight || weight <= 0) errors.push('Weight is required');

  if (errors.length > 0) {
    return { isValid: false, errors, warnings };
  }

  // Validate dimension ranges (typical courier limits)
  const minDimension = 1; // cm
  const minWeight = 1; // gram

  if (length < minDimension) errors.push(`Length must be at least ${minDimension}cm`);
  if (breadth < minDimension) errors.push(`Breadth must be at least ${minDimension}cm`);
  if (height < minDimension) errors.push(`Height must be at least ${minDimension}cm`);
  if (weight < minWeight) errors.push(`Weight must be at least ${minWeight}g`);

  // Check for unrealistic dimensions (e.g., very thin but heavy)
  const volume = length * breadth * height;
  if (volume > 0 && weight / volume > 10) {
    warnings.push('Package has very high density - please verify weight is in grams');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

const calculateVolumetricWeightGrams = (length, breadth, height) => {
  const l = parseFloat(length);
  const b = parseFloat(breadth);
  const h = parseFloat(height);

  if (![l, b, h].every(value => Number.isFinite(value) && value > 0)) {
    return 0;
  }

  return (l * b * h * 1000) / 5000;
};

const formatVolumetricWeightGrams = (weight) => {
  if (!Number.isFinite(weight) || weight <= 0) {
    return '0g';
  }

  const formatted = Number.isInteger(weight)
    ? weight.toString()
    : weight.toFixed(2).replace(/\.?0+$/, '');

  return `${formatted}g`;
};

/**
 * Formats error message for display in toast with source indicator
 * @param {Object} errorResponse - Error response from API or validation
 * @param {string} errorResponse.error - The error message
 * @param {string} errorResponse.error_source - 'SERVER' or 'PROZO'
 * @param {string} errorResponse.detail - Additional details
 * @returns {Object} Formatted error for toast display
 */
const formatLabelError = (errorResponse) => {
  const source = errorResponse?.error_source || 'SERVER';
  const error = errorResponse?.error || 'Unknown error occurred';
  const detail = errorResponse?.detail || '';

  return {
    source,
    message: error,
    detail,
    toastMessage: `[${source}] ${error}`
  };
};

const formatAddressLines = (addr) => {
  if (!addr) return [];

  const parts = [
    addr.address_line_1,
    addr.address_line_2,
    addr.city,
    addr.state,
    addr.pincode,
    addr.country,
  ].filter(part => part && part.trim() !== '');

  return parts;
};

// Normalize shipping address across possible API / UI shapes.
// This keeps the edit modal resilient when the "order" object doesn't carry a full
// `shipping_address` object but does have flattened fields like `shipTo`, `city`, etc.
const extractShippingAddressFromOrder = (order) => {
  if (!order) return {};

  // Common shapes we've seen:
  // - order.shipping_address = { address_line_1, ... }
  // - order.shipment.shipping_address = { address_line_1, ... }
  // - order.shipment.shipping_address.address = { address_line_1, ... }
  const raw =
    order.shipping_address ??
    order?.shipment?.shipping_address ??
    order?.shipment?.shipping_address?.address ??
    {};

  // Handle alternate key names defensively.
  const address_line_1 = raw.address_line_1 ?? raw.address1 ?? raw.line1 ?? order.shipTo ?? '';
  const address_line_2 = raw.address_line_2 ?? raw.address2 ?? raw.line2 ?? '';
  const city = raw.city ?? order.city ?? order.shippingCity ?? '';
  const state = raw.state ?? raw.province ?? order.state ?? '';
  const pincode = raw.pincode ?? raw.postal_code ?? raw.zip ?? order.pincode ?? '';
  const country = raw.country ?? order.shippingCountry ?? 'India';
  const phone = raw.phone ?? raw.phone_number ?? order.customerPhone ?? order.phone ?? '';
  const landmark = raw.landmark ?? '';
  const gstin = raw.gstin ?? '';
  // Extract address ID for in-place updates
  const id = raw.id ?? raw.address_id ?? raw.addressId ?? null;

  return {
    id,
    address_line_1,
    address_line_2,
    city,
    state,
    pincode,
    country,
    phone,
    landmark,
    gstin,
  };
};

// Shipping Address Modal Component
function ShippingAddressModal({ isOpen, onClose, order, onOrderAction, isReadOnly }) {
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [originalAddress, setOriginalAddress] = useState(null);
  const [form, setForm] = useState({
    nickname: 'Shipping',
    set_as_default: true,
    shipping_address: {
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
      phone: '',
      landmark: '',
      gstin: '',
    },
  });

  useEffect(() => {
    if (!isOpen || !order) return;

    const current = extractShippingAddressFromOrder(order);
    const normalizedAddress = {
      address_line_1: (current.address_line_1 || '').trim(),
      address_line_2: (current.address_line_2 || '').trim(),
      city: (current.city || '').trim(),
      state: (current.state || '').trim(),
      pincode: (current.pincode || '').trim(),
      country: (current.country || 'India').trim(),
      phone: (current.phone || '').trim(),
      landmark: (current.landmark || '').trim(),
      gstin: (current.gstin || '').trim(),
    };

    setOriginalAddress(normalizedAddress);
    setForm({
      nickname: 'Shipping',
      set_as_default: true,
      shipping_address: {
        id: current.id || null,
        address_line_1: current.address_line_1 || '',
        address_line_2: current.address_line_2 || '',
        city: current.city || '',
        state: current.state || '',
        pincode: current.pincode || '',
        country: current.country || 'India',
        phone: current.phone || '',
        landmark: current.landmark || '',
        gstin: current.gstin || '',
      },
    });

    setErrors({});
  }, [isOpen, order]);

  const validate = () => {
    const e = {};
    const a = form.shipping_address;

    // Address Line 1 validation
    if (!a.address_line_1.trim()) {
      e.address_line_1 = 'Address Line 1 is required';
    } else if (!ADDRESS_LINE_REGEX.test(a.address_line_1.trim())) {
      e.address_line_1 = 'Address contains invalid characters (use letters, numbers, spaces, commas, dots, hyphens, parentheses only)';
    } else if (a.address_line_1.trim().length < 5) {
      e.address_line_1 = 'Address Line 1 is too short (minimum 5 characters)';
    }

    // Address Line 2 validation (optional but check if provided)
    if (a.address_line_2 && a.address_line_2.trim() && !ADDRESS_LINE_REGEX.test(a.address_line_2.trim())) {
      e.address_line_2 = 'Address Line 2 contains invalid characters (use letters, numbers, spaces, commas, dots, hyphens, parentheses only)';
    }

    // City validation
    if (!a.city.trim()) {
      e.city = 'City is required';
    } else if (!CITY_REGEX.test(a.city.trim())) {
      e.city = 'City name contains invalid characters';
    }

    // State validation
    if (!a.state.trim()) {
      e.state = 'State is required';
    } else if (!STATE_REGEX.test(a.state.trim())) {
      e.state = 'State name contains invalid characters';
    }

    // Pincode validation (critical for shipping)
    if (!a.pincode.trim()) {
      e.pincode = 'Pincode is required';
    } else {
      const pincodeClean = a.pincode.replace(/\s/g, '');
      if (!PINCODE_REGEX.test(pincodeClean)) {
        e.pincode = 'Pincode must be a valid 6-digit Indian PIN code (100000-999999)';
      }
    }

    // Country validation
    if (!a.country.trim()) {
      e.country = 'Country is required';
    }

    // Phone validation (critical for delivery)
    if (!a.phone.trim()) {
      e.phone = 'Phone number is required';
    } else {
      const phoneClean = a.phone.replace(/[\s-]/g, '');
      const isValidPhone = PHONE_REGEX.test(phoneClean) || PHONE_WITH_COUNTRY_REGEX.test(phoneClean);
      if (!isValidPhone) {
        e.phone = 'Phone must be a valid 10-digit Indian mobile number (starts with 6-9) or +91 followed by 10 digits';
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const setAddressField = (field, value) => {
    setForm(prev => ({
      ...prev,
      shipping_address: {
        ...prev.shipping_address,
        [field]: value,
      },
    }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Compare normalized address fields to detect actual changes
  const hasAddressChanged = () => {
    if (!originalAddress) return true; // If no original address, assume changed

    const normalizedForm = {
      address_line_1: (form.shipping_address.address_line_1 || '').trim(),
      address_line_2: (form.shipping_address.address_line_2 || '').trim(),
      city: (form.shipping_address.city || '').trim(),
      state: (form.shipping_address.state || '').trim(),
      pincode: (form.shipping_address.pincode || '').trim(),
      country: (form.shipping_address.country || 'India').trim(),
      phone: (form.shipping_address.phone || '').trim(),
      landmark: (form.shipping_address.landmark || '').trim(),
      gstin: (form.shipping_address.gstin || '').trim(),
    };

    // Compare all address fields
    const addressFields = [
      'address_line_1',
      'address_line_2',
      'city',
      'state',
      'pincode',
      'country',
      'phone',
      'landmark',
      'gstin',
    ];

    for (const field of addressFields) {
      if (normalizedForm[field] !== originalAddress[field]) {
        return true;
      }
    }

    return false;
  };

  const handleSave = async () => {
    if (!order || isReadOnly) return;
    if (!validate()) return;

    // Check if address has actually changed
    if (!hasAddressChanged()) {
      // No changes detected, close modal without API call
      onClose();
      return;
    }

    setIsSaving(true);
    setErrors(prev => ({ ...prev, general: '' }));

    try {
      const payload = {
        shipping_address: {
          ...form.shipping_address,
          // Include address id for in-place updates (backend will use it if it matches current address)
          id: form.shipping_address.id || order.shipping_address?.id || null,
        },
        nickname: form.nickname,
        set_as_default: !!form.set_as_default,
      };

      const result = await updateOrderShippingAddress(order.id, payload);

      if (result && result.success) {
        if (onOrderAction) {
          onOrderAction('refresh_orders', order.id, {
            source: 'shipping_update',
            apiResponse: result,
          });
        }
        onClose();
      } else {
        setErrors(prev => ({ ...prev, general: result?.message || 'Failed to update shipping address' }));
      }
    } catch (err) {
      setErrors(prev => ({ ...prev, general: err.message || 'Failed to update shipping address' }));
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !order) return null;

  return (
    <div className="modalOverlay" onClick={onClose}>
      <div className="shippingAddressModal" onClick={(e) => e.stopPropagation()}>
        <div className="modalHeader">
          <h3>Edit Ship To Address</h3>
          <button className="closeModalBtn" onClick={onClose} disabled={isSaving} aria-label="Close modal">
            ×
          </button>
        </div>

        <div className="modalBody">
          {errors.general && (
            <div className="formGroup">
              <div className="errorMessage">{errors.general}</div>
            </div>
          )}

          <div className="addressFormGrid">
            <div className="formGroup addressFormColSpan2">
              <label>Address Line 1</label>
              <input
                type="text"
                value={form.shipping_address.address_line_1}
                onChange={(e) => setAddressField('address_line_1', e.target.value)}
                className={errors.address_line_1 ? 'error' : ''}
                disabled={isSaving || isReadOnly}
                placeholder="House no, street, area"
              />
              {errors.address_line_1 && <div className="errorMessage">{errors.address_line_1}</div>}
            </div>

            <div className="formGroup addressFormColSpan2">
              <label>Address Line 2</label>
              <input
                type="text"
                value={form.shipping_address.address_line_2}
                onChange={(e) => setAddressField('address_line_2', e.target.value)}
                className={errors.address_line_2 ? 'error' : ''}
                disabled={isSaving || isReadOnly}
                placeholder="Optional: Apartment, suite, unit, building, floor, etc."
              />
              {errors.address_line_2 && <div className="errorMessage">{errors.address_line_2}</div>}
            </div>

            <div className="formGroup">
              <label>City</label>
              <input
                type="text"
                value={form.shipping_address.city}
                onChange={(e) => setAddressField('city', e.target.value)}
                className={errors.city ? 'error' : ''}
                disabled={isSaving || isReadOnly}
              />
              {errors.city && <div className="errorMessage">{errors.city}</div>}
            </div>

            <div className="formGroup">
              <label>State</label>
              <input
                type="text"
                value={form.shipping_address.state}
                onChange={(e) => setAddressField('state', e.target.value)}
                className={errors.state ? 'error' : ''}
                disabled={isSaving || isReadOnly}
              />
              {errors.state && <div className="errorMessage">{errors.state}</div>}
            </div>

            <div className="formGroup">
              <label>Pincode</label>
              <input
                type="text"
                value={form.shipping_address.pincode}
                onChange={(e) => setAddressField('pincode', e.target.value)}
                className={errors.pincode ? 'error' : ''}
                disabled={isSaving || isReadOnly}
              />
              {errors.pincode && <div className="errorMessage">{errors.pincode}</div>}
            </div>

            <div className="formGroup">
              <label>Country</label>
              <input
                type="text"
                value={form.shipping_address.country}
                onChange={(e) => setAddressField('country', e.target.value)}
                className={errors.country ? 'error' : ''}
                disabled={isSaving || isReadOnly}
              />
              {errors.country && <div className="errorMessage">{errors.country}</div>}
            </div>

            <div className="formGroup">
              <label>Phone</label>
              <input
                type="text"
                value={form.shipping_address.phone}
                onChange={(e) => setAddressField('phone', e.target.value)}
                className={errors.phone ? 'error' : ''}
                disabled={isSaving || isReadOnly}
                placeholder="+91-9999999999"
              />
              {errors.phone && <div className="errorMessage">{errors.phone}</div>}
            </div>

            <div className="formGroup">
              <label>Landmark</label>
              <input
                type="text"
                value={form.shipping_address.landmark}
                onChange={(e) => setAddressField('landmark', e.target.value)}
                disabled={isSaving || isReadOnly}
                placeholder="Optional"
              />
            </div>

            <div className="formGroup">
              <label>GSTIN</label>
              <input
                type="text"
                value={form.shipping_address.gstin}
                onChange={(e) => setAddressField('gstin', e.target.value)}
                disabled={isSaving || isReadOnly}
                placeholder="Optional"
              />
            </div>
          </div>
        </div>

        <div className="modalFooter">
          <button className="cancelBtn" onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          <button className={`saveBtn ${isSaving ? 'loading' : ''}`} onClick={handleSave} disabled={isSaving || isReadOnly}>
            {isSaving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Pickup Address Modal Component
function PickupAddressModal({ isOpen, onClose, order, onOrderAction }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [pickupAddresses, setPickupAddresses] = useState([]);
  const [selectedPickupId, setSelectedPickupId] = useState(null);

  useEffect(() => {
    if (!isOpen || !order) return;

    let cancelled = false;

    const loadPickups = async () => {
      setLoading(true);
      setError(null);
      try {
        // Get vendor_id from the order
        const vendorId = order?.vendorId || order?.vendor?.id;
        if (!vendorId) {
          throw new Error('Vendor ID not found in order');
        }

        const data = await fetchVendorPickupAddresses(vendorId);
        if (cancelled) return;

        const addresses = data.pickup_addresses || [];
        setPickupAddresses(addresses);

        // Check both order.pickup_address and order.shipment.pickup_address for backward compatibility
        const currentPickupId = order?.pickup_address?.id || order?.shipment?.pickup_address?.id || null;
        let initialId = currentPickupId;

        if (!initialId && addresses.length > 0) {
          const def = addresses.find(p => p.is_default);
          initialId = def?.id || addresses[0].id;
        }

        setSelectedPickupId(initialId || '');
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to load pickup addresses');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPickups();

    return () => {
      cancelled = true;
    };
  }, [isOpen, order]);

  const handleChange = (e) => {
    const newId = e.target.value;
    setSelectedPickupId(newId);
  };

  const selectedPickup = pickupAddresses.find(p => p.id === selectedPickupId);

  const handleSave = async () => {
    if (!order || !selectedPickupId) return;

    try {
      setSaving(true);
      setError(null);
      const result = await updateOrderPickupAddress(order.id, selectedPickupId);

      if (result && result.success) {
        if (onOrderAction) {
          onOrderAction('refresh_orders', order.id, {
            source: 'pickup_update',
            apiResponse: result,
          });
        }
        onClose();
      } else {
        setError(result?.message || 'Failed to update pickup address');
      }
    } catch (err) {
      setError(err.message || 'Failed to update pickup address');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !order) return null;

  return (
    <div className="modalOverlay" onClick={onClose}>
      <div className="pickupAddressModal" onClick={(e) => e.stopPropagation()}>
        <div className="modalHeader">
          <h3>Select Pickup Address</h3>
          <button className="closeModalBtn" onClick={onClose} disabled={saving}>×</button>
        </div>
        <div className="modalBody">
          {error && (
            <div className="formGroup">
              <div className="errorMessage">{error}</div>
            </div>
          )}

          {loading ? (
            <div className="formGroup">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div className="spinner" style={{ width: '20px', height: '20px', border: '2px solid var(--gray-300)', borderTop: '2px solid var(--primary-orange)', borderRadius: '50%' }}></div>
                <span>Loading pickup addresses...</span>
              </div>
            </div>
          ) : (
            <div className="formGroup">
              <label>Available Pickup Locations</label>
              {pickupAddresses.length === 0 ? (
                <div style={{ fontSize: '14px', color: 'var(--gray-600)', padding: '12px', background: 'var(--gray-100)', borderRadius: '6px', textAlign: 'center' }}>
                  <strong>No pickup addresses configured</strong><br />
                  <span style={{ fontSize: '12px', color: 'var(--gray-500)' }}>Please configure pickup addresses in vendor settings</span>
                </div>
              ) : (
                <select
                  className="shipperDropdown"
                  value={selectedPickupId || ''}
                  onChange={handleChange}
                  disabled={saving}
                >
                  <option value="" disabled>
                    Select a pickup location
                  </option>
                  {pickupAddresses.map((pickup) => (
                    <option key={pickup.id} value={pickup.id}>
                      {pickup.warehouse_name || 'Unnamed pickup location'}
                      {pickup.is_default ? ' (Default)' : ''}
                    </option>
                  ))}
                </select>
              )}

              {selectedPickup && selectedPickup.address && (
                <div className="addressPreviewCard">
                  <div className="addressPreviewTitle">
                    {selectedPickup.warehouse_name || 'Pickup Location'}
                    {selectedPickup.is_default ? ' (Default)' : ''}
                  </div>
                  <div className="addressPreviewLines">
                    {formatAddressLines(selectedPickup.address).map((line, idx) => (
                      <div key={idx} className="addressPreviewLine">{line}</div>
                    ))}
                    {selectedPickup.address.phone && (
                      <div className="addressPreviewLine addressPreviewMuted">Phone: {selectedPickup.address.phone}</div>
                    )}
                  </div>
                </div>
              )}

              {pickupAddresses.length > 0 && (
                <div className="helpText">
                  Select a pickup location and click Save to update this order.
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modalFooter">
          <button className="cancelBtn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="saveBtn" onClick={handleSave} disabled={saving || !selectedPickupId}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Manual Shipping Details Modal Component
function ManualShippingDetailsModal({ isOpen, onClose, order, onOrderAction, editableDimensions }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [pickupAddresses, setPickupAddresses] = useState([]);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    pickupAddressId: '',
    awbNumber: '',
    shippingPartner: '',
  });

  useEffect(() => {
    if (!isOpen || !order) return;

    let cancelled = false;

    const loadPickups = async () => {
      setLoading(true);
      setError(null);
      try {
        // Get vendor_id from the order
        const vendorId = order?.vendorId || order?.vendor?.id;
        if (!vendorId) {
          throw new Error('Vendor ID not found in order');
        }

        const data = await fetchVendorPickupAddresses(vendorId);
        if (cancelled) return;

        const addresses = data.pickup_addresses || [];
        setPickupAddresses(addresses);

        // Check if order already has manual shipping details
        const hasManualShipping = order.shipment &&
          (order.shipment.carrier?.name === 'MANUAL' || order.shipment.carrier?.code === 'MANUAL');

        if (hasManualShipping) {
          // Pre-fill form with existing data from API (without dimensions - they come from parent)
          setForm({
            pickupAddressId: order.shipment.pickup_address?.id || order.pickup_address?.id || '',
            awbNumber: order.shipment.awb_number || order.awbNumber || '',
            shippingPartner: order.shipment.shipping_partner || '',
            trackingLink: order.trackingLink || '',
          });
        } else {
          // Reset form for new entry - set default pickup if available
          let defaultPickupId = '';
          if (addresses.length > 0) {
            const currentPickupId = order?.pickup_address?.id || order?.shipment?.pickup_address?.id;
            const def = addresses.find(p => p.is_default);
            defaultPickupId = currentPickupId || def?.id || addresses[0].id || '';
          }

          setForm({
            pickupAddressId: defaultPickupId,
            awbNumber: '',
            shippingPartner: '',
            trackingLink: '',
          });
        }
        setErrors({});
      } catch (err) {
        if (cancelled) {
          return;
        }
        setError(err.message || 'Failed to load pickup addresses');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPickups();

    return () => {
      cancelled = true;
    };
  }, [isOpen, order]);

  const validate = () => {
    const e = {};

    if (!form.pickupAddressId) {
      e.pickupAddressId = 'Pickup address is required';
    }

    if (!form.awbNumber.trim()) {
      e.awbNumber = 'AWB number is required';
    } else if (form.awbNumber.trim().length < 5) {
      e.awbNumber = 'AWB number is too short';
    }

    if (!form.shippingPartner.trim()) {
      e.shippingPartner = 'Shipping partner is required';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleFieldChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSave = async () => {
    if (!order) return;
    if (!validate()) return;

    try {
      setSaving(true);
      setError(null);

      // Get dimensions from editableDimensions or order properties
      const dimensions = editableDimensions?.[order.id] || {};
      const length = dimensions.length ?? order.length;
      const breadth = dimensions.breadth ?? order.breadth;
      const height = dimensions.height ?? order.height;
      const weight = dimensions.weight ?? order.weight;

      // Build payload
      const payload = {
        pickup_address_id: form.pickupAddressId,
        awb_number: form.awbNumber,
        shipping_partner: form.shippingPartner,
        tracking_link: form.trackingLink.trim(),
      };

      // Add optional dimension fields if provided
      if (length && String(length).trim()) payload.length_cm = parseFloat(length);
      if (breadth && String(breadth).trim()) payload.breadth_cm = parseFloat(breadth);
      if (height && String(height).trim()) payload.height_cm = parseFloat(height);
      if (weight && String(weight).trim()) payload.weight_grams = parseFloat(weight);

      const result = await addManualShippingDetails(order.id, payload);

      showToast({
        type: 'success',
        message: result?.message || 'Manual shipping details saved successfully',
      });

      if (onOrderAction) {
        onOrderAction('refresh_orders', order.id, { source: 'manual_shipping_update', apiResponse: result });
      }

      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save manual shipping details');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !order) return null;

  const selectedPickup = pickupAddresses.find(p => p.id === form.pickupAddressId);
  const hasManualShipping = order.shipment &&
    (order.shipment.carrier?.name === 'MANUAL' || order.shipment.carrier?.code === 'MANUAL');

  return (
    <div className="modalOverlay" onClick={onClose}>
      <div className="pickupAddressModal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '90%' }}>
        <div className="modalHeader">
          <h3>{hasManualShipping ? 'Change Manual Shipping Details' : 'Add Manual Shipping Details'}</h3>
          <button className="closeModalBtn" onClick={onClose} disabled={saving}>×</button>
        </div>
        <div className="modalBody">
          {error && (
            <div className="formGroup">
              <div className="errorMessage">{error}</div>
            </div>
          )}

          {loading ? (
            <div className="formGroup">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div className="spinner" style={{ width: '20px', height: '20px', border: '2px solid var(--gray-300)', borderTop: '2px solid var(--primary-orange)', borderRadius: '50%' }}></div>
                <span>Loading pickup addresses...</span>
              </div>
            </div>
          ) : (
            <>
              <div className="formGroup">
                <label>Pickup Address *</label>
                {pickupAddresses.length === 0 ? (
                  <div style={{ fontSize: '14px', color: 'var(--gray-600)', padding: '12px', background: 'var(--gray-100)', borderRadius: '6px', textAlign: 'center' }}>
                    <strong>No pickup addresses configured</strong><br />
                    <span style={{ fontSize: '12px', color: 'var(--gray-500)' }}>Please configure pickup addresses in vendor settings</span>
                  </div>
                ) : (
                  <>
                    <select
                      className={`shipperDropdown ${errors.pickupAddressId ? 'error' : ''}`}
                      value={form.pickupAddressId}
                      onChange={(e) => handleFieldChange('pickupAddressId', e.target.value)}
                      disabled={saving}
                    >
                      <option value="">Select a pickup location</option>
                      {pickupAddresses.map((pickup) => (
                        <option key={pickup.id} value={pickup.id}>
                          {pickup.warehouse_name || 'Unnamed pickup location'}
                          {pickup.is_default ? ' (Default)' : ''}
                        </option>
                      ))}
                    </select>
                    {errors.pickupAddressId && <div className="errorMessage">{errors.pickupAddressId}</div>}
                  </>
                )}

                {selectedPickup && selectedPickup.address && (
                  <div className="addressPreviewCard" style={{ marginTop: '8px' }}>
                    <div className="addressPreviewTitle">
                      {selectedPickup.warehouse_name || 'Pickup Location'}
                      {selectedPickup.is_default ? ' (Default)' : ''}
                    </div>
                    <div className="addressPreviewLines">
                      {formatAddressLines(selectedPickup.address).map((line, idx) => (
                        <div key={idx} className="addressPreviewLine">{line}</div>
                      ))}
                      {selectedPickup.address.phone && (
                        <div className="addressPreviewLine addressPreviewMuted">Phone: {selectedPickup.address.phone}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="formGroup">
                <label>AWB Number *</label>
                <input
                  type="text"
                  value={form.awbNumber}
                  onChange={(e) => handleFieldChange('awbNumber', e.target.value)}
                  className={errors.awbNumber ? 'error' : ''}
                  disabled={saving}
                  placeholder="Enter AWB/tracking number"
                />
                {errors.awbNumber && <div className="errorMessage">{errors.awbNumber}</div>}
              </div>

              <div className="formGroup">
                <label>Shipping Partner *</label>
                <input
                  type="text"
                  value={form.shippingPartner}
                  onChange={(e) => handleFieldChange('shippingPartner', e.target.value)}
                  className={errors.shippingPartner ? 'error' : ''}
                  disabled={saving}
                  placeholder="e.g., DTDC, Delhivery, Blue Dart"
                />
                {errors.shippingPartner && <div className="errorMessage">{errors.shippingPartner}</div>}
              </div>

              <div className="formGroup">
                <label>Tracking Link <span style={{ fontSize: '12px', color: 'var(--gray-500)', fontWeight: 'normal' }}>(optional)</span></label>
                <input
                  type="text"
                  value={form.trackingLink}
                  onChange={(e) => handleFieldChange('trackingLink', e.target.value)}
                  disabled={saving}
                  placeholder="e.g. https://www.bluedart.com/tracking/AWB123456"
                />
              </div>
            </>
          )}
        </div>
        <div className="modalFooter">
          <button className="cancelBtn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="saveBtn"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function OrderListTable({
  orders,
  onOrderAction,
  onOrderSelect,
  onSelectAll,
  onSelectAllGlobal,
  onClearSelection,
  selectAllMode = false,
  totalCount = 0,
  selectedOrders = [],
  editableDimensions,
  selectedProzoChannels: parentSelectedChannels,
  onProzoChannelChange: onParentProzoChannelChange,
  onDimensionChange,
  isReadOnly,
  userRole,
  isLoading,
  userId,
  activeTab,
  canConfirmOrders = false,
  canCancelOrders = false,
  canEditShipToAddresses = false,
  sortField,
  sortDirection,
  onSort
}) {
  const navigate = useNavigate();
  // Only admins may move an order back to "New Order"; the option is hidden for vendors.
  const isAdmin = userRole === 'admin';
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  // Which leg the fulfillment column is showing, keyed by order id. Index 0 is
  // the newest shipment, which is the active one in every ordinary case.
  const [showStatusDropdown, setShowStatusDropdown] = useState({});
  const [showEmailDropdown, setShowEmailDropdown] = useState({});
  const [addressVerifEmailSent, setAddressVerifEmailSent] = useState({});
  const [showInvoiceDropdown, setShowInvoiceDropdown] = useState({});
  const [selectedProzoChannel, setSelectedProzoChannel] = useState({});
  const [selectedProviderName, setSelectedProviderName] = useState({});

  // Use parent state if provided, otherwise fall back to local state
  const selectedChannels = parentSelectedChannels || selectedProzoChannel;
  const handleChannelChange = onParentProzoChannelChange || ((orderId, channelName) => {
    setSelectedProzoChannel(prev => ({ ...prev, [orderId]: channelName }));
  });
  const [generatingLabel, setGeneratingLabel] = useState({});
  const [orderLabelStatus, setOrderLabelStatus] = useState({});
  const [downloadingDocuments, setDownloadingDocuments] = useState({});
  // Shown after label generation while Prozo prepares the manifest.
  const [retryingManifest, setRetryingManifest] = useState({});
  const [generatingInvoice, setGeneratingInvoice] = useState({});
  const [downloadingInvoice, setDownloadingInvoice] = useState({});
  const [cancellingInvoice, setCancellingInvoice] = useState({});
  const [orderInvoiceStatus, setOrderInvoiceStatus] = useState({});
  // Store IDs rather than the full order object.
  // This prevents stale snapshots (e.g., if `orders` gets refreshed async while the modal is open).
  const [shipToModal, setShipToModal] = useState({ isOpen: false, orderId: null });
  const [pickupModal, setPickupModal] = useState({ isOpen: false, orderId: null });
  const [showShippingCostModal, setShowShippingCostModal] = useState({ isOpen: false, orderId: null });
  const [manualShippingModal, setManualShippingModal] = useState({ isOpen: false, orderId: null });
  // mode 'required' = opened because a label/AWB attempt was refused for a missing prescription.
  const [prescriptionModal, setPrescriptionModal] = useState({ isOpen: false, orderId: null, mode: 'upload' });
  const [shippingCosts, setShippingCosts] = useState({});
  const [isManual, setIsManual] = useState({}); // Track manual selection per order
  const [trackingLinkEdit, setTrackingLinkEdit] = useState({});
  const [tagsPopup, setTagsPopup] = useState({ visible: false, tags: [], x: 0, y: 0 });

  // Follow-up / Penalty state
  const [followUpModal, setFollowUpModal] = useState({ isOpen: false, orderId: null, action: null });
  const [followUpNotes, setFollowUpNotes] = useState('');
  const [followUpLoading, setFollowUpLoading] = useState(false);
  const [showFollowUpDropdown, setShowFollowUpDropdown] = useState({});

  // Reset local invoice cache when orders change so we don't override fresh backend state
  useEffect(() => {
    setOrderInvoiceStatus({});
  }, [orders]);

  // Sync manual-shipping channel selection when orders change (must be in useEffect, not render)
  useEffect(() => {
    orders.forEach(order => {
      const hasManualShipping = order.shipment &&
        (order.shipment.carrier?.name === 'MANUAL' || order.shipment.carrier?.code === 'MANUAL');
      if (hasManualShipping) {
        handleChannelChange(order.id, MANUAL_CHANNEL_VALUE);
        setIsManual(prev => {
          if (prev[order.id]) return prev;
          return { ...prev, [order.id]: true };
        });
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders]);

  // Auto-enable the combined download once Prozo's manifest lands.
  //
  // Refreshing the list replaces `orders`, which re-runs this effect — so the
  // attempt counter lives in a ref keyed by the pending order IDs. Without that
  // it would reset every tick and poll forever on a permanently-failed manifest.
  const manifestPollRef = useRef({ key: '', attempts: 0 });
  useEffect(() => {
    const pendingIds = orders
      .filter(o => o.shipment?.label_url && !o.shipment?.manifest_url)
      .map(o => o.id)
      .sort();
    if (pendingIds.length === 0) {
      manifestPollRef.current = { key: '', attempts: 0 };
      return;
    }

    const key = pendingIds.join(',');
    if (manifestPollRef.current.key !== key) {
      manifestPollRef.current = { key, attempts: 0 };
    }
    // ~60s ceiling, matching the backend's limited post-label retry budget.
    if (manifestPollRef.current.attempts >= 6) return;

    const timer = setTimeout(() => {
      manifestPollRef.current.attempts += 1;
      onOrderAction?.('refresh_orders', null, { source: 'manifest_poll' });
    }, 10000);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders]);

  const handleTrackingLinkEdit = (orderId, currentLink) => {
    setTrackingLinkEdit(prev => ({ ...prev, [orderId]: { isEditing: true, value: currentLink || '', saving: false } }));
  };

  const handleTrackingLinkCancel = (orderId) => {
    setTrackingLinkEdit(prev => ({ ...prev, [orderId]: { ...prev[orderId], isEditing: false } }));
  };

  const handleTrackingLinkSave = async (orderId, newValue) => {
    setTrackingLinkEdit(prev => ({ ...prev, [orderId]: { ...prev[orderId], saving: true } }));
    try {
      const { updateTrackingLink } = await import('../../api/api');
      await updateTrackingLink(orderId, newValue.trim());
      setTrackingLinkEdit(prev => ({ ...prev, [orderId]: { isEditing: false, value: newValue.trim(), saving: false } }));
      if (onOrderAction) onOrderAction('refresh_orders', orderId, { source: 'tracking_link_update', trackingLink: newValue.trim() });
    } catch (err) {
      setTrackingLinkEdit(prev => ({ ...prev, [orderId]: { ...prev[orderId], saving: false } }));
      showToast({ type: 'error', message: err.message || 'Failed to save tracking link' });
    }
  };

  // Follow-up / Penalty handlers
  const toggleFollowUpDropdown = (orderId) => {
    setShowFollowUpDropdown(prev => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  const openFollowUpModal = (orderId, action) => {
    const order = orders.find(o => o.id === orderId);
    // Pre-fill with existing exception note when revoking (so admin can see why it was granted)
    const initialNotes = (action === 'exception' && order?.exceptionActive) ? (order?.exceptionNote || '') : '';
    setFollowUpModal({ isOpen: true, orderId, action });
    setFollowUpNotes(initialNotes);
    setShowFollowUpDropdown({});
  };

  const closeFollowUpModal = () => {
    setFollowUpModal({ isOpen: false, orderId: null, action: null });
    setFollowUpNotes('');
  };

  const getFollowUpStageLabel = (stage) => {
    const labels = {
      'none': 'None',
      'first_follow_up': '1st Follow Up',
      'second_follow_up': '2nd Follow Up',
      'exception': 'Exception',
      'penalty': 'Penalty',
    };
    return labels[stage] || stage;
  };

  const handleFollowUpSubmit = async () => {
    const { orderId, action } = followUpModal;
    if (!orderId || !action) return;
    setFollowUpLoading(true);
    try {
      let result;
      let patch = {};
      if (action === 'exception') {
        const order = orders.find(o => o.id === orderId);
        if (order?.exceptionActive) {
          result = await revokeOrderException(orderId);
          patch = {
            exceptionActive: false,
            exceptionNote: '',
            exceptionRaisedAt: null,
            followUpStage: 'none',
          };
          showToast({ type: 'success', message: result.message || 'Exception revoked' });
        } else {
          result = await setOrderException(orderId, followUpNotes);
          patch = {
            exceptionActive: true,
            exceptionNote: result.exception_note || followUpNotes,
            exceptionRaisedAt: result.exception_raised_at || null,
            followUpStage: result.follow_up_stage || 'exception',
          };
          showToast({ type: 'success', message: result.message || 'Exception granted' });
        }
      } else if (action === 'penalty') {
        result = await applyOrderPenalty(orderId, 10);
        patch = {
          followUpStage: 'penalty',
          penaltyPercentage: result.penalty_percentage || 10,
          followUpStage: result.follow_up_stage || 'penalty',
        };
        showToast({ type: 'success', message: `Penalty of ${result.penalty_percentage || 10}% applied` });
      } else {
        const stageApiMap = { first_follow_up: 'first', second_follow_up: 'second' };
        result = await sendOrderFollowUp(orderId, stageApiMap[action] || action, followUpNotes);
        const stageToFlag = {
          first_follow_up: 'firstFollowupSent',
          second_follow_up: 'secondFollowupSent',
        };
        // Normalize short backend stage names to long frontend form
        const _stageNorm = { first: 'first_follow_up', second: 'second_follow_up' };
        const normalizedStage = _stageNorm[result.follow_up_stage] || result.follow_up_stage || action;
        patch = {
          followUpStage: normalizedStage,
          ...(stageToFlag[action] ? { [stageToFlag[action]]: true } : {}),
        };
        showToast({ type: 'success', message: `${getFollowUpStageLabel(action)} sent successfully${result.email_sent ? ' (email sent)' : ''}` });
      }
      closeFollowUpModal();
      // Instant optimistic update — no page reload needed
      if (onOrderAction) onOrderAction('update_order', orderId, { patch });
    } catch (err) {
      showToast({ type: 'error', message: err.message || 'Failed to process action' });
    } finally {
      setFollowUpLoading(false);
    }
  };

  const getFollowUpModalTitle = (action) => {
    if (action === 'exception') {
      const order = orders.find(o => o.id === followUpModal.orderId);
      return order?.exceptionActive ? 'Revoke Exception' : 'Grant Exception';
    }
    const titles = {
      'first_follow_up': 'Send First Follow-Up',
      'second_follow_up': 'Send Second Follow-Up',
      'penalty': 'Apply Penalty',
    };
    return titles[action] || 'Follow-Up Action';
  };

  const getFollowUpModalDescription = (action, order) => {
    const brandName = order?.vendor?.company_name || order?.vendorName || 'the vendor';
    const orderNum = order?.order_number || order?.orderNumber;
    if (action === 'exception') {
      if (order?.exceptionActive) {
        return `This will revoke the active exception for ${brandName} on order #${orderNum}. Follow-up reminders will resume after revoking.`;
      }
      return `This will grant an exception to ${brandName} for order #${orderNum}. Follow-up reminders will be paused while the exception is active.`;
    }
    const descriptions = {
      'first_follow_up': `This will send a first follow-up reminder to ${brandName} regarding delayed processing of order #${orderNum}.`,
      'second_follow_up': `This will send a second follow-up reminder to ${brandName}. This is the second reminder for order #${orderNum}.`,
      'penalty': `This will impose a penalty on ${brandName} for delayed processing of order #${orderNum}. An email notification will be sent to the brand.`,
    };
    return descriptions[action] || '';
  };

  const handleFetchShippingCosts = async (orderId) => {
    // Only fetch if not already fetched or loading
    if (activeTab !== 'confirmed' && activeTab !== 'new_orders') return;
    if (shippingCosts[orderId] && shippingCosts[orderId].costs && shippingCosts[orderId].costs.length > 0) return;

    // Get order details
    const order = orders.find(o => o.id === orderId);
    if (!order) return;

    // Get dimensions from editable state or order object
    const dims = editableDimensions[orderId] || {};
    const length = parseFloat(dims.length || order.length || 0);
    const breadth = parseFloat(dims.breadth || order.breadth || 0);
    const height = parseFloat(dims.height || order.height || 0);
    const weight = parseFloat(dims.weight || order.weight || 0);

    // Validate dimensions
    if (length <= 0 || breadth <= 0 || height <= 0 || weight <= 0) {
      // Logic moved to checkDimensionsValid for UI state, keeping this as safety
      return;
    }

    // Prepare payload
    // Extract pincodes
    // Pickup address pincode
    const pickupPincode = order.pickup_address?.address?.pincode ||
      (order.pickupAddress && order.pickupAddress.match(/\d{6}/)?.[0]) || '';

    // Delivery address pincode
    const shippingAddr = extractShippingAddressFromOrder(order);
    const deliveryPincode = shippingAddr.pincode || '';

    if (!pickupPincode || !deliveryPincode) {
      showToast({ type: 'error', message: 'Pickup or Delivery Pincode missing' });
      return;
    }

    setShippingCosts(prev => ({
      ...prev,
      [orderId]: { loading: true, costs: [], error: null }
    }));

    try {
      const payload = {
        order_id: orderId,
        length,
        breadth,
        height,
        weight, // Backend expects weight in grams;
      };

      const response = await calculateShippingCost(payload);
      const normalized = normalizeShippingCostCalculatorResponse(response);

      setShippingCosts(prev => ({
        ...prev,
        [orderId]: {
          loading: false,
          costs: normalized.costs,
          meta: normalized.meta ?? null,
          error: null,
        },
      }));
    } catch (error) {
      console.error("Error calculating shipping costs", error);
      // The browser surfaces network failures as the literal "Failed to fetch",
      // which is confusing in the shipping context (it looks like a serviceability
      // result). Translate it into a clear connectivity message so an actual
      // unserviceable lane (handled as an empty, non-error result) stays distinct.
      const rawMessage = error?.message || '';
      const friendlyMessage = /failed to fetch|networkerror|load failed/i.test(rawMessage)
        ? 'Could not reach the shipping service. Please check your connection and try again.'
        : rawMessage || 'Failed to calculate shipping cost';
      setShippingCosts(prev => ({
        ...prev,
        [orderId]: { loading: false, costs: [], error: friendlyMessage }
      }));
    }
  };

  const getShippingCostForChannel = (orderId, channelName) => {
    const data = shippingCosts[orderId];
    if (!data || !data.costs) return null;

    const costsForUi = excludeDisabledShippingPartners(data.costs);

    // Match a quote whose own resolved channel equals the requested channelName, so the
    // price for e.g. "bluedart-air" resolves to the AIR quote, not the SURFACE one.
    const match = costsForUi.find((c) => {
      const resolved = resolveProzoChannel(
        c.provider?.parent,
        c.provider?.dispatch_mode || c.dispatch_mode,
        c.provider?.service_type_primary
      );
      return resolved === channelName;
    });

    return match ? match.priceAfterTax : null;
  };

  const handleOrderClick = (orderId) => {
    navigate(`/suspire-terminal/orders/${orderId}`);
  };

  const handleSortClick = (field) => {
    if (onSort) {
      onSort(field);
    }
  };

  const handleSelectAll = (e) => {
    if (onSelectAll) {
      onSelectAll(e.target.checked);
    }
  };

  const handleOrderSelect = (orderId, isSelected) => {
    if (onOrderSelect) {
      onOrderSelect(orderId, isSelected);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getPaymentStatusClass = (status) => {
    switch (status.toUpperCase()) {
      case 'PAID':
        return 'statusPaid';
      case 'UNPAID':
        return 'statusUnpaid';
      case 'PENDING':
        return 'statusPending';
      case 'COD':
        return 'statusCOD';
      default:
        return 'statusDefault';
    }
  };

  // Handle tags popup
  const handleTagsClick = (e, tags) => {
    e.stopPropagation();
    const { clientX, clientY } = e;
    setTagsPopup({
      visible: true,
      tags: tags,
      x: clientX,
      y: clientY
    });
  };

  // Close tags popup
  const closeTagsPopup = () => {
    setTagsPopup({ visible: false, tags: [], x: 0, y: 0 });
  };

  const getOrderStatusClass = (status) => {
    const normalized = status.toLowerCase().replace(/ /g, '_');
    switch (normalized) {
      case 'new_orders':
      case 'new_order':
      case 'new':
        return 'statusNewOrders';
      case 'confirmed':
        return 'statusConfirmed';
      case 'ready_to_ship':
        return 'statusReadyToShip';
      case 'in_transit':
        return 'statusInTransit';
      case 'delivered':
        return 'statusDelivered';
      case 'exceptions':
        return 'statusException';
      case 'cancelled':
        return 'statusCancelled';
      case 'rto':
        return 'statusRTO';
      case 'held':
        return 'statusHeld';
      case 'lost':
        return 'statusLost';
      case 'damaged':
        return 'statusDamaged';
      default:
        return 'statusDefault';
    }
  };

  const getPaymentMode = (order) => {
    // Prefer API financial status; fall back to mapped paymentStatus from adapter
    const financialStatus = (order.financialStatus || order.financial_status || order.paymentStatus || '')
      .toString()
      .toLowerCase();
    // FIX: Changed label from 'Prepaid' to 'Paid' to match user expectations after 'Mark as Paid' action.
    return financialStatus === 'paid' ? 'Paid' : 'Cash On Delivery';
  };

  const getPaymentModeClass = (mode) => {
    return mode === 'Paid' ? 'paymentModePP' : 'paymentModeCOD';
  };

  // Check if all orders on current page are selected
  const currentPageOrderIds = orders.map(order => order.id);

  // When in selectAllMode, all items should appear checked
  const allCurrentPageSelected = selectAllMode || (
    currentPageOrderIds.length > 0 &&
    currentPageOrderIds.every(id => selectedOrders.includes(id))
  );
  const someCurrentPageSelected = selectAllMode || currentPageOrderIds.some(id => selectedOrders.includes(id));

  const isAllSelected = allCurrentPageSelected;
  const isPartiallySelected = !selectAllMode && someCurrentPageSelected && !allCurrentPageSelected;

  const handleHeaderCheckboxClick = (e) => {
    e.stopPropagation();
    const shouldSelectAll = !isAllSelected;
    if (onSelectAll) {
      onSelectAll(shouldSelectAll);
    }
  };

  if (isLoading) {
    return (
      <div className="tableContainer">
        <div className="emptyState">
          <div className="spinner"></div>
          <p>Loading orders...</p>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="tableContainer">
        <div className="emptyState">
          <div className="emptyStateContent">
            <p>No orders found</p>
            <p className="emptyStateSubtext">
              Try adjusting your filters or search criteria
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Determine what to show based on active tab
  const showShipperInfo = ['ready_to_ship', 'in_transit', 'exceptions', 'delivered', 'all', 'rto', 'lost', 'damaged'].includes(activeTab);
  const showAWB = ['ready_to_ship', 'in_transit', 'exceptions', 'delivered', 'all', 'held', 'rto', 'lost', 'damaged'].includes(activeTab);

  /**
   * Page the fulfillment column through the order's shipments.
   *
   * Mirrors handleImageScroll (the product carousel) so both arrow controls in
   * a row behave identically — wrapping at either end rather than dead-ending.
   */
  const handleImageScroll = (orderId, direction) => {
    const order = orders.find(o => o.id === orderId);
    const totalImages = order?.productMediaEntries?.length || order?.productImages?.length;
    if (!order || !totalImages) return;

    setCurrentImageIndex(prev => {
      const currentIdx = prev[orderId] || 0;
      const cappedCurrent = Math.min(currentIdx, totalImages - 1);
      let newIdx;

      if (direction === 'left') {
        newIdx = cappedCurrent === 0 ? totalImages - 1 : cappedCurrent - 1;
      } else {
        newIdx = cappedCurrent === totalImages - 1 ? 0 : cappedCurrent + 1;
      }

      return { ...prev, [orderId]: newIdx };
    });
  };

  const toggleStatusDropdown = (orderId) => {
    setShowStatusDropdown(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const toggleEmailDropdown = (orderId) => {
    setShowEmailDropdown(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const handleProzoChannelChange = (orderId, channelName, providerName = '') => {
    handleChannelChange(orderId, channelName);

    if (providerName) {
      setSelectedProviderName(prev => ({
        ...prev,
        [orderId]: providerName
      }));
    }

    // Track if manual option is selected
    setIsManual(prev => ({
      ...prev,
      [orderId]: channelName === MANUAL_CHANNEL_VALUE
    }));
  };

  const handleDimensionChange = (orderId, field, value) => {
    if (onDimensionChange) {
      onDimensionChange(orderId, field, value);
    }

    // Reset shipping costs when dimensions change
    // This will force the API to be recalled when user clicks on shipping cost modal
    setShippingCosts(prev => ({
      ...prev,
      [orderId]: { loading: false, costs: [], error: null }
    }));
  };

  const handleGenerateLabel = async (orderId) => {
    const channelName = selectedChannels[orderId];
    const providerName = selectedProviderName[orderId];

    if (!channelName || channelName === 'none') {
      showToast({ type: 'error', message: 'Please select a PROZO channel first' });
      return;
    }

    // Get the current order
    const currentOrder = orders.find(o => o.id === orderId);
    if (!currentOrder) {
      showToast({ type: 'error', message: 'Order not found' });
      return;
    }

    // Prescription orders cannot get a label until the prescription is uploaded.
    if (orderNeedsPrescription(currentOrder)) {
      openPrescriptionModal(orderId, 'required');
      return;
    }

    // Check if pickup address is null, N/A, or not set
    const pickupAddrObj = currentOrder?.pickup_address?.address;
    const pickupAddressValue = currentOrder?.pickupAddress;
    const hasValidPickupAddress = pickupAddrObj &&
      pickupAddrObj !== 'N/A' &&
      pickupAddrObj !== 'No Address' &&
      pickupAddressValue &&
      pickupAddressValue !== 'N/A' &&
      pickupAddressValue !== 'No Address';

    if (!hasValidPickupAddress) {
      console.error('[SERVER] Validation Error: Pickup address is not set');
      showToast({
        type: 'error',
        message: '[SERVER] Pickup address is not set. Please set a pickup address before generating a label.',
        duration: 5000
      });
      return;
    }

    // Extract addresses for validation
    const pickupAddress = currentOrder?.pickup_address?.address || {};
    const shippingAddress = extractShippingAddressFromOrder(currentOrder);

    // Validate pickup address format
    const pickupValidation = validateAddressForProzo(pickupAddress, 'Pickup');
    if (!pickupValidation.isValid) {
      const errorMsg = pickupValidation.errors[0];
      console.error('[SERVER] Validation Error:', errorMsg);
      showToast({
        type: 'error',
        message: `[SERVER] ${errorMsg}`,
        duration: 5000
      });
      return;
    }

    // Validate shipping address format
    const shippingValidation = validateAddressForProzo(shippingAddress, 'Delivery');
    if (!shippingValidation.isValid) {
      const errorMsg = shippingValidation.errors[0];
      console.error('[SERVER] Validation Error:', errorMsg);
      showToast({
        type: 'error',
        message: `[SERVER] ${errorMsg}`,
        duration: 5000
      });
      return;
    }

    // Get the current dimensions from editable state or order
    const currentDimensions = editableDimensions[orderId] || {};
    const length = parseFloat(currentDimensions.length || currentOrder?.length || 0);
    const breadth = parseFloat(currentDimensions.breadth || currentOrder?.breadth || 0);
    const height = parseFloat(currentDimensions.height || currentOrder?.height || 0);
    const weight = parseFloat(currentDimensions.weight || currentOrder?.weight || 0);

    // Validate dimensions
    const dimensionValidation = validatePackageDimensions(length, breadth, height, weight);
    if (!dimensionValidation.isValid) {
      const errorMsg = dimensionValidation.errors[0];
      console.error('[SERVER] Validation Error:', errorMsg);
      showToast({
        type: 'error',
        message: `[SERVER] ${errorMsg}`,
        duration: 5000
      });
      return;
    }

    // Show warnings if any (for debugging purposes)
    const allWarnings = [
      ...pickupValidation.warnings,
      ...shippingValidation.warnings,
      ...dimensionValidation.warnings
    ];
    if (allWarnings.length > 0) {
      console.warn('[SERVER] Validation Warnings:', allWarnings);
    }

    try {
      setGeneratingLabel(prev => ({ ...prev, [orderId]: true }));

      // Construct composite carrier name: "Prozo-{providerName}"
      const namePart = providerName || channelName;
      const formattedNamePart = namePart.replace(/\s+/g, '-');
      const compositeShipperName = `Prozo-${formattedNamePart}`;

      const response = await generateShippingLabel(orderId, PROZO_CARRIER_NAME, channelName, breadth, length, height, weight, compositeShipperName);

      if (response.success) {
        showToast({
          type: 'success',
          message: `Label generated successfully! AWB: ${response.awb_code || response.awb_number || 'N/A'}`,
          duration: 5000
        });

        // The manifest is produced asynchronously by Prozo after the AWB is indexed,
        // so set the expectation instead of letting the vendor click a download that
        // would 404 for the next few seconds.
        showToast({
          type: 'success',
          message: 'Manifest is being prepared. The Download Manifest & Label button will enable automatically in about 30 seconds.',
          duration: 8000,
        });

        // Update local state to reflect label generation
        setOrderLabelStatus(prev => ({
          ...prev,
          [orderId]: {
            labelGenerated: true,
            awbCode: response.awb_code || response.awb_number,
            labelUrl: response.label_url,
            carrier: response.carrier || response.carrier_name
          }
        }));

        // Trigger parent refresh
        if (onOrderAction) {
          onOrderAction('refresh_orders', orderId, { response });
        }
      } else if (response.error_code === 'PRESCRIPTION_REQUIRED') {
        // The server knows of a prescription requirement this row did not (e.g. the
        // tag was added since the list loaded) — ask for the upload instead of a toast.
        openPrescriptionModal(orderId, 'required');
      } else {
        // Handle error response with clear source indicator
        const formattedError = formatLabelError(response);

        // Log with source indicator for debugging
        if (formattedError.source === 'PROZO') {
          console.error('[PROZO ERROR]', formattedError.message, formattedError.detail);
        } else {
          console.error('[SERVER ERROR]', formattedError.message, formattedError.detail);
        }

        showToast({
          type: 'error',
          message: formattedError.toastMessage,
          duration: 6000
        });
      }
    } catch (error) {
      // This should rarely happen since API now returns error object instead of throwing
      console.error('[SERVER ERROR] Unexpected error:', error.message);
      showToast({
        type: 'error',
        message: `[SERVER] ${error.message || 'Failed to generate label'}`,
        duration: 6000
      });
    } finally {
      setGeneratingLabel(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const handleRemoveLabel = async (orderId) => {
    if (!window.confirm('Are you sure you want to remove this shipping label? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await removeShippingLabel(orderId);
      if (response && response.success) {
        showToast({
          type: 'success',
          message: 'Shipping label removed successfully',
          duration: 3000
        });

        // Update Local state to remove Label Info
        setOrderLabelStatus(prev => ({
          ...prev,
          [orderId]: {
            labelGenerated: false,
            awbCode: null,
            labelUrl: null,
            carrier: null
          }
        }));

        // Trigger Parent Refresh
        if (onOrderAction) {
          onOrderAction('refresh_orders', orderId, { source: 'cancel_label' });
        }
      } else {
        showToast({
          type: 'error',
          message: response?.message || 'Failed to remove shipping label'
        });
      }
    } catch (error) {
      console.error("error removing label:", error);
      showToast({
        type: 'error',
        message: error.message || 'Failed to cancel shipping label'
      });
    }
  }

  const handleDownloadLabel = (order) => {
    // Get label URL from order data or local state
    const labelUrl = orderLabelStatus[order.id]?.labelUrl || order.shipment?.label_url;

    if (labelUrl) {
      window.open(labelUrl, '_blank');
    } else {
      showToast({ type: 'error', message: 'Label URL not available' });
    }
  };

  const handleDownloadManifest = (order) => {
    // Get manifest URL from order shipment data
    const manifestUrl = order.shipment?.manifest_url;

    if (manifestUrl) {
      window.open(manifestUrl, '_blank');
    } else {
      showToast({ type: 'error', message: 'Manifest URL not available' });
    }
  };

  const handleGenerateInvoice = async (orderId) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) {
      showToast({ type: 'error', message: 'Order not found' });
      return;
    }

    // Basic guard: require a generated label before allowing invoice
    const hasLabel =
      !!(orderLabelStatus[orderId]?.labelGenerated) ||
      !!order.labelGenerated ||
      !!order.shipment?.label_url;

    const hasManualShipping = order.shipment &&
      (order.shipment.carrier?.name === 'MANUAL' || order.shipment.carrier?.code === 'MANUAL');

    if (!hasLabel && !hasManualShipping) {
      showToast({
        type: 'error',
        message: 'Generate a shipping label or add manual shipping details before creating an invoice for this order.',
      });
      return;
    }

    try {
      setGeneratingInvoice(prev => ({ ...prev, [orderId]: true }));
      const bulkResult = await generateInvoices([orderId]);
      const results = bulkResult?.results || [];
      const entry = results.find(r => r.order_id === String(orderId));

      if (!entry || entry.status !== 'success') {
        const errorMessage =
          entry?.error ||
          bulkResult?.error ||
          'Failed to generate invoice for this order.';
        throw new Error(errorMessage);
      }

      showToast({
        type: 'success',
        message: 'Invoice generated successfully.',
      });

      const invoiceData = entry.invoice || {};
      setOrderInvoiceStatus(prev => ({
        ...prev,
        [orderId]: {
          hasInvoice: true,
          invoiceNumber: invoiceData.invoice_number,
          invoiceUrl: invoiceData.order_invoice_url,
        },
      }));
    } catch (error) {
      showToast({
        type: 'error',
        message: error.message || 'Failed to generate invoice. Please try again.',
      });
    } finally {
      setGeneratingInvoice(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const handleDownloadInvoice = async (orderId) => {
    try {
      setDownloadingInvoice(prev => ({ ...prev, [orderId]: true }));

      // Prefer cached URL if we already fetched or generated an invoice in this session
      const cached = orderInvoiceStatus[orderId];
      if (cached?.invoiceUrl) {
        window.open(cached.invoiceUrl, '_blank');
        return;
      }

      const data = await getOrderInvoice(orderId);
      if (data && data.invoice_url) {
        setOrderInvoiceStatus(prev => ({
          ...prev,
          [orderId]: {
            hasInvoice: true,
            invoiceNumber: data.invoice_number,
            invoiceUrl: data.invoice_url,
          },
        }));
        window.open(data.invoice_url, '_blank');
      } else {
        showToast({
          type: 'error',
          message: data?.error || 'Invoice URL not available for this order.',
        });
      }
    } catch (error) {
      showToast({
        type: 'error',
        message: error.message || 'Failed to download invoice.',
      });
    } finally {
      setDownloadingInvoice(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const handleCancelInvoice = async (orderId) => {
    try {
      setCancellingInvoice(prev => ({ ...prev, [orderId]: true }));
      const result = await cancelInvoices([orderId]);
      const results = result?.results || [];
      const entry = results.find(r => r.order_id === String(orderId));

      if (!entry || entry.status !== 'success') {
        const errorMessage =
          entry?.error ||
          result?.error ||
          'Failed to cancel invoice for this order.';
        throw new Error(errorMessage);
      }

      showToast({
        type: 'success',
        message: 'Invoice cancelled successfully.',
      });

      setOrderInvoiceStatus(prev => ({
        ...prev,
        [orderId]: {
          hasInvoice: false,
          invoiceNumber: null,
          invoiceUrl: null,
        },
      }));
    } catch (error) {
      showToast({
        type: 'error',
        message: error.message || 'Failed to cancel invoice. Please try again.',
      });
    } finally {
      setCancellingInvoice(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const handlePickupAddressEdit = (order) => {
    setPickupModal({
      isOpen: true,
      orderId: order?.id || null,
    });
  };

  const closePickupModal = () => {
    setPickupModal({ isOpen: false, orderId: null });
  };

  const handleShipToAddressEdit = (order) => {
    setShipToModal({
      isOpen: true,
      orderId: order?.id || null,
    });
  };

  const closeShipToModal = () => {
    setShipToModal({ isOpen: false, orderId: null });
  };

  const handleGetShippingCost = (orderId) => {
    setShowShippingCostModal({ isOpen: true, orderId });
  };

  const closeShippingCostModal = () => {
    setShowShippingCostModal({ isOpen: false, orderId: null });
  };

  const handleOpenManualShipping = (orderId) => {
    // A manual AWB ships the order just like a label, so the same gate applies.
    if (orderNeedsPrescription(orders.find(o => o.id === orderId))) {
      openPrescriptionModal(orderId, 'required');
      return;
    }
    setManualShippingModal({ isOpen: true, orderId });
  };

  const openPrescriptionModal = (orderId, mode = 'upload') => {
    setPrescriptionModal({ isOpen: true, orderId, mode });
  };

  const closePrescriptionModal = () => {
    setPrescriptionModal({ isOpen: false, orderId: null, mode: 'upload' });
  };

  const closeManualShippingModal = () => {
    setManualShippingModal({ isOpen: false, orderId: null });
  };

  const shipToOrder = shipToModal.orderId ? orders.find(o => o.id === shipToModal.orderId) : null;
  const pickupOrder = pickupModal.orderId ? orders.find(o => o.id === pickupModal.orderId) : null;
  const manualShippingOrder = manualShippingModal.orderId ? orders.find(o => o.id === manualShippingModal.orderId) : null;
  const prescriptionOrder = prescriptionModal.orderId ? orders.find(o => o.id === prescriptionModal.orderId) : null;

  // Shipping Cost Modal Component
  function ShippingCostModal({ isOpen, onClose, orderId }) {
    if (!isOpen || !orderId) return null;

    const costData = shippingCosts[orderId] || {};
    const meta = costData.meta || null;
    // Serviceable-only quotes from API; then apply disabled-partner exclusions for display (same as original flow).
    const costs = excludeDisabledShippingPartners(costData.costs || []);
    const isLoading = costData.loading;
    const error = costData.error;

    const order = orders.find(o => o.id === orderId);
    const orderDims = editableDimensions[orderId] || {};
    const length = orderDims.length || order?.length || 0;
    const breadth = orderDims.breadth || order?.breadth || 0;
    const height = orderDims.height || order?.height || 0;
    const weight = orderDims.weight || order?.weight || 0;


    return (
      <div className="modalOverlay" onClick={onClose}>
        <div className="shippingCostModal" onClick={(e) => e.stopPropagation()}>
            <div className="modalHeader">
            <div>
              <h3>Shipping Cost Details</h3>
              <div className="orderInfoSubtext">Order #{order?.order_number || orderId}</div>
              {meta && (
                <div className="orderInfoSubtext" style={{ marginTop: '6px', maxWidth: '420px' }}>
                  Serviceable carriers only ({meta.payment_type || '—'})
                  {meta.lane ? ` · ${meta.lane}` : ''}
                  {typeof meta.serviceable_option_count === 'number'
                    ? ` · ${meta.serviceable_option_count} option(s)`
                    : ''}
                </div>
              )}
            </div>
            <button className="closeModalBtn" onClick={onClose} aria-label="Close modal">
              ×
            </button>
          </div>

          <div className="modalBody">
            {/* Package Dimensions Summary */}
            <div className="dimensionsSummary">
              <h4>Package Dimensions</h4>
              <div className="dimensionsGrid">
                <div className="dimensionItem">
                  <span className="dimensionLabel">Length:</span>
                  <span className="dimensionValue">{length} cm</span>
                </div>
                <div className="dimensionItem">
                  <span className="dimensionLabel">Breadth:</span>
                  <span className="dimensionValue">{breadth} cm</span>
                </div>
                <div className="dimensionItem">
                  <span className="dimensionLabel">Height:</span>
                  <span className="dimensionValue">{height} cm</span>
                </div>
                <div className="dimensionItem">
                  <span className="dimensionLabel">Weight:</span>
                  <span className="dimensionValue">{weight} g ({(weight / 1000).toFixed(2)} kg)</span>
                </div>
              </div>
            </div>

            {isLoading && (
              <div className="loadingState">
                <div className="spinner"></div>
                <p>Calculating shipping costs...</p>
              </div>
            )}

            {error && (
              <div className="errorState">
                <div className="errorIcon">⚠️</div>
                <p className="errorMessage">{error}</p>
              </div>
            )}

            {!isLoading && !error && costs.length === 0 && (
              <div className="emptyState">
                <div className="emptyIcon">📦</div>
                <p>No serviceable shipping options</p>
                <p className="emptySubtext">
                  {meta && meta.filtered_out_quote_count > 0
                    ? 'Quotes exist from Prozo but no carrier is serviceable on this lane for the current payment mode. Check pincodes or payment type (PREPAID vs COD).'
                    : 'Please verify package dimensions and pincodes.'}
                </p>
                <button
                  className="manualShippingBtnPrimary"
                  onClick={() => {
                    onClose();
                    handleOpenManualShipping(orderId);
                  }}
                  style={{ marginTop: '20px' }}
                >
                  📝 Add Shipping Details Manually
                </button>
              </div>
            )}

            {/* Manual Shipping Option Card */}
            {!isLoading && (
              <div className="manualShippingCard">
                <div className="manualShippingIcon">📝</div>
                <div className="manualShippingContent">
                  <div className="manualShippingTitle">Add Shipping Details Manually</div>
                </div>
                <button
                  className="manualShippingBtn"
                  onClick={() => {
                    onClose();
                    handleOpenManualShipping(orderId);
                  }}
                >
                  Add Manually →
                </button>
              </div>
            )}

            {!isLoading && !error && costs.length > 0 && (
              <div className="shippingOptionsContainer">
                <h4>Serviceable carriers — pricing ({costs.length})</h4>

                <div className="costCardsGrid">
                  {costs.map((cost, idx) => {
                        const providerName = cost.provider?.parent || cost.provider?.name || 'Unknown';
                        const providerId = cost.provider?.id || '';
                        // Prefer Prozo's human remarks, else the service_type_primary code,
                        // else fall back to zone / payment mode.
                        const serviceTypePrimary = cost.provider?.service_type_primary || '';
                        const serviceName =
                          cost.provider?.remarks
                          || serviceTypePrimary
                          || cost.zone
                          || cost.paymentMode
                          || 'Standard';
                        // Use the carrier's real mode (provider.dispatch_mode); the
                        // top-level dispatch_mode mirrors it but provider is authoritative.
                        const dispatchMode = cost.provider?.dispatch_mode || cost.dispatch_mode || '';
                        const dispatchModeLabel = dispatchMode.toUpperCase();
                        const basePrice = cost.priceBeforeTax || 0;
                        const finalPrice = cost.priceAfterTax || cost.amount || 0;
                        const taxAmount = cost.tax || (finalPrice - basePrice);
                        const taxPercentage = basePrice > 0 ? ((taxAmount / basePrice) * 100).toFixed(1) : 0;
                        const minTatDays = cost.providerMinTatDays || cost.minTatDays || 0;
                        const maxTatDays = cost.providerMaxTatDays || cost.maxTatDays || 0;
                        const paymentMode = cost.paymentMode || '';
                        const zone = cost.zone || '';

                        return (
                          <div key={idx} className="costCard">
                            <div className="costCardHeader">
                              <div className="providerInfo">
                                <div className="providerName">{providerName}</div>
                                <div className="serviceName">{serviceName}</div>
                                {providerId && (
                                  <div className="providerId">ID: {providerId}</div>
                                )}
                              </div>
                              <div className="costCardPrice">
                                <div className="finalPrice">₹{finalPrice.toFixed(2)}</div>
                                {basePrice !== finalPrice && (
                                  <div className="basePrice">Base: ₹{basePrice.toFixed(2)}</div>
                                )}
                              </div>
                            </div>

                            <div className="costCardDetails">
                              {/* Service type + dispatch mode (from Prozo service_type_primary) */}
                              {(serviceTypePrimary || dispatchModeLabel) && (
                                <div className="detailRow">
                                  <span className="detailLabel">Service:</span>
                                  <span className="detailValue">
                                    {serviceTypePrimary || '—'}
                                    {dispatchModeLabel ? ` · ${dispatchModeLabel}` : ''}
                                  </span>
                                </div>
                              )}

                              {/* Tax Information */}
                              {taxAmount > 0 && (
                                <div className="detailRow">
                                  <span className="detailLabel">Tax ({taxPercentage}%):</span>
                                  <span className="detailValue">₹{taxAmount.toFixed(2)}</span>
                                </div>
                              )}

                              {/* Delivery Time (TAT) */}
                              {(minTatDays > 0 || maxTatDays > 0) && (
                                <div className="detailRow">
                                  <span className="detailLabel">Delivery Time:</span>
                                  <span className="detailValue highlight">
                                    {minTatDays === maxTatDays
                                      ? `${minTatDays} day${minTatDays > 1 ? 's' : ''}`
                                      : `${minTatDays}-${maxTatDays} days`}
                                  </span>
                                </div>
                              )}

                              {/* Payment Mode */}
                              {paymentMode && (
                                <div className="detailRow">
                                  <span className="detailLabel">Payment Mode:</span>
                                  <span className="detailValue">{paymentMode}</span>
                                </div>
                              )}

                              {/* Zone */}
                              {zone && (
                                <div className="detailRow">
                                  <span className="detailLabel">Zone:</span>
                                  <span className="detailValue">{zone}</span>
                                </div>
                              )}

                              {/* COD Available */}
                              {cost.cod_available !== undefined && (
                                <div className="detailRow">
                                  <span className="detailLabel">COD Available:</span>
                                  <span className={`detailValue ${cost.cod_available ? 'success' : 'muted'}`}>
                                    {cost.cod_available ? '✓ Yes' : '✗ No'}
                                  </span>
                                </div>
                              )}

                              {/* Mode (if different from service) */}
                              {cost.mode && cost.mode !== serviceName && (
                                <div className="detailRow">
                                  <span className="detailLabel">Mode:</span>
                                  <span className="detailValue">{cost.mode}</span>
                                </div>
                              )}
                            </div>

                            <button
                              className="selectProviderBtn"
                              onClick={() => {
                                // Resolve to the channel of the quote's dispatch bucket
                                // (surface / air / sdd-ndd) so an AIR quote never books a
                                // SURFACE channel.
                                const channelName = resolveProzoChannel(
                                  providerName,
                                  dispatchMode,
                                  cost.provider?.service_type_primary
                                );

                                if (channelName) {
                                  handleProzoChannelChange(orderId, channelName);
                                  onClose();
                                  showToast({
                                    type: 'success',
                                    message: `Selected ${providerName} - ${serviceName}`
                                  });
                                } else {
                                  showToast({
                                    type: 'warning',
                                    message: 'This provider may need manual selection from dropdown'
                                  });
                                }
                              }}
                            >
                              Select This Option
                            </button>
                          </div>
                        );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="modalFooter">
            <button className="cancelBtn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="orderTableContainer">
      {/* Column Headers */}
      <div className="orderTableHeaders">
        <div className="headerCheckbox">
          <input
            type="checkbox"
            checked={isAllSelected}
            ref={input => {
              if (input) {
                input.indeterminate = isPartiallySelected;
              }
            }}
            onChange={handleHeaderCheckboxClick}
            onClick={(e) => e.stopPropagation()}
            disabled={isReadOnly || orders.length === 0}
            className="headerCheckboxInput"
          />
        </div>
        <div
          className={`headerColumn sortableHeader ${sortField === 'createdAt' || sortField === 'created_at' ? 'active' : ''}`}
          role="columnheader"
          aria-sort={
            sortField === 'createdAt' || sortField === 'created_at'
              ? (sortDirection === 'asc' ? 'ascending' : 'descending')
              : 'none'
          }
        >
          <button
            type="button"
            onClick={() => handleSortClick('createdAt')}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || (event.key === ' ' && !event.repeat)) {
                event.preventDefault();
                handleSortClick('createdAt');
              }
            }}
            aria-label={
              sortField === 'createdAt' || sortField === 'created_at'
                ? `Sort by order details, currently in ${sortDirection === 'asc' ? 'ascending' : 'descending'} order`
                : 'Sort by order details, no sort applied'
            }
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              margin: 0,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              color: 'inherit',
              font: 'inherit',
              textAlign: 'inherit',
              cursor: 'pointer',
            }}
          >
            <span>Order Details</span>
            <div className="sortIconsContainer">
              <div className="inactiveIcons">
                <Triangle size={6} fill="currentColor" />
                <Triangle size={6} fill="currentColor" style={{ transform: 'rotate(180deg)' }} />
              </div>
              {(sortField === 'createdAt' || sortField === 'created_at') && (
                sortDirection === 'asc'
                  ? <Triangle size={8} fill="currentColor" className="sortIcon active" style={{ marginLeft: '2px' }} />
                  : <Triangle size={8} fill="currentColor" className="sortIcon active" style={{ marginLeft: '2px', transform: 'rotate(180deg)' }} />
              )}
            </div>
          </button>
        </div>
        <div
          className={`headerColumn sortableHeader ${sortField === 'channel' ? 'active' : ''}`}
          role="columnheader"
          aria-sort={
            sortField === 'channel'
              ? (sortDirection === 'asc' ? 'ascending' : 'descending')
              : 'none'
          }
        >
          <button
            type="button"
            onClick={() => handleSortClick('channel')}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || (event.key === ' ' && !event.repeat)) {
                event.preventDefault();
                handleSortClick('channel');
              }
            }}
            aria-label={
              sortField === 'channel'
                ? `Sort by channel, currently in ${sortDirection === 'asc' ? 'ascending' : 'descending'} order`
                : 'Sort by channel, no sort applied'
            }
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              margin: 0,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              color: 'inherit',
              font: 'inherit',
              textAlign: 'inherit',
              cursor: 'pointer',
            }}
          >
            <span>Channel</span>
            <div className="sortIconsContainer">
              <div className="inactiveIcons">
                <Triangle size={6} fill="currentColor" />
                <Triangle size={6} fill="currentColor" style={{ transform: 'rotate(180deg)' }} />
              </div>
              {sortField === 'channel' && (
                sortDirection === 'asc'
                  ? <Triangle size={8} fill="currentColor" className="sortIcon active" style={{ marginLeft: '2px' }} />
                  : <Triangle size={8} fill="currentColor" className="sortIcon active" style={{ marginLeft: '2px', transform: 'rotate(180deg)' }} />
              )}
            </div>
          </button>
        </div>
        <div
          className={`headerColumn sortableHeader ${sortField === 'product' ? 'active' : ''}`}
          role="columnheader"
          aria-sort={
            sortField === 'product'
              ? (sortDirection === 'asc' ? 'ascending' : 'descending')
              : 'none'
          }
        >
          <button
            type="button"
            onClick={() => handleSortClick('product')}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || (event.key === ' ' && !event.repeat)) {
                event.preventDefault();
                handleSortClick('product');
              }
            }}
            aria-label={
              sortField === 'product'
                ? `Sort by product, currently in ${sortDirection === 'asc' ? 'ascending' : 'descending'} order`
                : 'Sort by product, no sort applied'
            }
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              margin: 0,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              color: 'inherit',
              font: 'inherit',
              textAlign: 'inherit',
              cursor: 'pointer',
            }}
          >
            <span>Product</span>
            <div className="sortIconsContainer">
              <div className="inactiveIcons">
                <Triangle size={6} fill="currentColor" />
                <Triangle size={6} fill="currentColor" style={{ transform: 'rotate(180deg)' }} />
              </div>
              {sortField === 'product' && (
                sortDirection === 'asc'
                  ? <Triangle size={8} fill="currentColor" className="sortIcon active" style={{ marginLeft: '2px' }} />
                  : <Triangle size={8} fill="currentColor" className="sortIcon active" style={{ marginLeft: '2px', transform: 'rotate(180deg)' }} />
              )}
            </div>
          </button>
        </div>
        <div
          className={`headerColumn sortableHeader ${sortField === 'payment' ? 'active' : ''}`}
          role="columnheader"
          aria-sort={
            sortField === 'payment'
              ? (sortDirection === 'asc' ? 'ascending' : 'descending')
              : 'none'
          }
        >
          <button
            type="button"
            onClick={() => handleSortClick('payment')}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || (event.key === ' ' && !event.repeat)) {
                event.preventDefault();
                handleSortClick('payment');
              }
            }}
            aria-label={
              sortField === 'payment'
                ? `Sort by payment, currently in ${sortDirection === 'asc' ? 'ascending' : 'descending'} order`
                : 'Sort by payment, no sort applied'
            }
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              margin: 0,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              color: 'inherit',
              font: 'inherit',
              textAlign: 'inherit',
              cursor: 'pointer',
            }}
          >
            <span>Payment</span>
            <div className="sortIconsContainer">
              <div className="inactiveIcons">
                <Triangle size={6} fill="currentColor" />
                <Triangle size={6} fill="currentColor" style={{ transform: 'rotate(180deg)' }} />
              </div>
              {sortField === 'payment' && (
                sortDirection === 'asc'
                  ? <Triangle size={8} fill="currentColor" className="sortIcon active" style={{ marginLeft: '2px' }} />
                  : <Triangle size={8} fill="currentColor" className="sortIcon active" style={{ marginLeft: '2px', transform: 'rotate(180deg)' }} />
              )}
            </div>
          </button>
        </div>
        <div
          className={`headerColumn sortableHeader ${sortField === 'customer' ? 'active' : ''}`}
          role="columnheader"
          aria-sort={
            sortField === 'customer'
              ? (sortDirection === 'asc' ? 'ascending' : 'descending')
              : 'none'
          }
        >
          <button
            type="button"
            onClick={() => handleSortClick('customer')}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || (event.key === ' ' && !event.repeat)) {
                event.preventDefault();
                handleSortClick('customer');
              }
            }}
            aria-label={
              sortField === 'customer'
                ? `Sort by customer, currently in ${sortDirection === 'asc' ? 'ascending' : 'descending'} order`
                : 'Sort by customer, no sort applied'
            }
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              margin: 0,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              color: 'inherit',
              font: 'inherit',
              textAlign: 'inherit',
              cursor: 'pointer',
            }}
          >
            <span>Customer</span>
            <div className="sortIconsContainer">
              <div className="inactiveIcons">
                <Triangle size={6} fill="currentColor" />
                <Triangle size={6} fill="currentColor" style={{ transform: 'rotate(180deg)' }} />
              </div>
              {sortField === 'customer' && (
                sortDirection === 'asc'
                  ? <Triangle size={8} fill="currentColor" className="sortIcon active" style={{ marginLeft: '2px' }} />
                  : <Triangle size={8} fill="currentColor" className="sortIcon active" style={{ marginLeft: '2px', transform: 'rotate(180deg)' }} />
              )}
            </div>
          </button>
        </div>
        <div className="headerColumn">Address</div>
        <div className="headerColumn">Fulfillment</div>
        <div className="headerColumn">Status & Actions</div>
      </div>

      <div className="orderTableWrapper">
        {orders.map((order) => {
          const paymentMode = getPaymentMode(order);

          const carouselLength = order.productMediaEntries?.length || order.productImages?.length || 1;
          const currentIdxRaw = currentImageIndex[order.id] || 0;
          const currentIdx = Math.min(currentIdxRaw, Math.max(carouselLength - 1, 0));
          const currentMedia = order.productMediaEntries?.[currentIdx];
          const imageSrc = currentMedia?.image || (order.productImages ? order.productImages[currentIdx] : order.productImage);
          const displayName = currentMedia?.name || order.productName;
          const displayQty = currentMedia?.quantity ?? order.itemCount;
          const indicatorCount = order.productMediaEntries?.length || order.productImages?.length || 0;
          const imageInitial = (displayName || '').charAt(0).toUpperCase() || 'P';

          // Check if label exists from API data or local state
          const hasLabel = !!(orderLabelStatus[order.id]?.labelGenerated) ||
            !!order.labelGenerated ||
            !!order.shipment?.label_url;

          const labelUrl = orderLabelStatus[order.id]?.labelUrl ||
            (order.shipment && order.shipment.label_url);

          // Invoice status: prefer optimistic local state after actions, otherwise backend-provided status
          const localInvoiceState = orderInvoiceStatus[order.id];
          const hasInvoice =
            localInvoiceState?.hasInvoice !== undefined
              ? !!localInvoiceState.hasInvoice
              : !!order.invoice?.has_active;

          // Check if order has manual shipping details.
          // Manual shipping sets awb_number but never label_url, so accept either the
          // MANUAL carrier or a bare AWB — this mirrors the backend's Ready-to-Ship rule.
          const hasManualShipping = !!order.shipment &&
            (order.shipment.carrier?.name === 'MANUAL' ||
              order.shipment.carrier?.code === 'MANUAL' ||
              !!order.shipment.awb_number);

          // Ready to Ship needs a dispatchable shipment: a generated label OR manual details.
          const canMarkReadyToShip = hasLabel || hasManualShipping;

          // The combined download needs BOTH documents. Prozo produces the manifest
          // asynchronously after the AWB is indexed, so label_url can exist alone.
          // getManifestState splits that window into 'preparing' (recent, still
          // coming) vs 'stuck' (past the retry budget, likely never) so the button
          // stops showing "Preparing documents…" forever on a dead manifest.
          const hasManifest = !!order.shipment?.manifest_url;

          // One order, one label. Exception legs (returns, replacements) live on
          // the admin-only exception-labels screen and are deliberately absent
          // from the vendor-facing row.
          const manifestState = getManifestState(order.shipment);
          const docsReady = manifestState === 'ready';
          const manifestStuck = manifestState === 'stuck';

          const blockReadyToShip = () => showToast({
            type: 'error',
            message: 'Label not generated for this order. Generate label or add manual shipping details before marking Ready to Ship.',
          });

          // NOTE: channel/isManual sync for manual-shipping orders is handled in the useEffect
          // that watches [orders]. Calling setState here during render causes
          // "Cannot update ManageOrders while rendering OrderListTable" in React 18 concurrent mode.

          // Determine if addresses can be edited (only before label generation and before ready_to_ship)
          const orderStatus = (order.orderStatus || '').toLowerCase().replace(' ', '_');
          const isConfirmedOrEarlier = ['new_orders', 'confirmed'].includes(orderStatus);
          const canEditAddresses = !hasLabel && isConfirmedOrEarlier;

          // Check dimensions validity for UI state
          const dimsValid = (() => {
            const dims = editableDimensions[order.id] || {};
            const l = parseFloat(dims.length || order.length || 0);
            const b = parseFloat(dims.breadth || order.breadth || 0);
            const h = parseFloat(dims.height || order.height || 0);
            const w = parseFloat(dims.weight || order.weight || 0);
            return l > 0 && b > 0 && h > 0 && w > 0;
          })();

          const costState = shippingCosts[order.id] || {};
          const isCostLoading = costState.loading;
          const hasCosts = costState.costs && costState.costs.length > 0;
          const currentDimensions = editableDimensions[order.id] || {};
          const volumetricWeightLabel = formatVolumetricWeightGrams(
            calculateVolumetricWeightGrams(
              currentDimensions.length || order.length,
              currentDimensions.breadth || order.breadth,
              currentDimensions.height || order.height
            )
          );

          return (
            <div key={order.id} className="orderCard">
              {/* Checkbox */}
              <div className="orderCheckbox">
                <input
                  type="checkbox"
                  checked={selectAllMode || selectedOrders.includes(order.id)}
                  onChange={(e) => handleOrderSelect(order.id, e.target.checked)}
                  disabled={isReadOnly}
                />
              </div>

              {/* Order Details Section */}
              <div className="orderSection orderDetailsSection">
                <div className="orderNumberRow">
                  <div
                    className="orderNumber"
                    onClick={() => handleOrderClick(order.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleOrderClick(order.id);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    aria-label={`View details for order ${order.orderNumber}`}
                  >
                    {order.orderNumber}
                  </div>
                  <button className="editIconBtn" title="Edit Order">✏️</button>
                </div>
                <div className="orderDateTime">{order.orderDate} | {order.orderTime}</div>
                <div className="channelInfo">
                  <div className="channelTag">
                    <span className="channelIcon">
                      {order.channel === 'Shopify' && '🛍️'}
                      {order.channel === 'WooCommerce' && '🛒'}
                      {order.channel === 'Amazon' && '📦'}
                      {order.channel === 'Custom' && '⚙️'}
                    </span>
                    <span className="channelName">{order.channel}</span>
                  </div>
                </div>

                {/* Tags Display */}
                {order.tags && order.tags.length > 0 && (
                  <div className="orderTagsContainer" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.25rem' }}>
                    {order.tags.slice(0, 2).map((tag, index) => (
                      <button
                        key={index}
                        type="button"
                        className="orderTagBadge"
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => handleTagsClick(e, order.tags)}
                        title="Click to view all tags"
                      >
                        {tag}
                      </button>
                    ))}
                    {order.tags.length > 2 && (
                      <button
                        type="button"
                        className="orderTagBadge orderTagMore"
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => handleTagsClick(e, order.tags)}
                        title="Click to view all tags"
                      >
                        +{order.tags.length - 2}
                      </button>
                    )}
                  </div>
                )}

                {/* Cancellation Reason Display (only for cancelled orders) */}
                {(order.rawStatus === 'cancelled' || order.orderStatus === 'Cancelled') && (
                  <div className="orderTagsContainer">
                    <span className="orderTagBadge" style={{
                      background: '#ffebee',
                      color: '#c62828',
                      border: '1px solid #ef9a9a'
                    }}>
                      🚫 {order.cancellation_reason}
                    </span>
                  </div>
                )}

                {/* Vendor Shopify sync. Compact here — the id and any failure
                    reason ride in the tooltip, since these rows are dense. The
                    server sends null when this viewer shouldn't see it. */}
                {(() => {
                  const sync = vendorSyncDisplay(order.vendorShopifySync);
                  if (!sync) return null;
                  const tooltip = [
                    sync.orderId ? `Vendor Shopify order #${sync.orderId}` : null,
                    sync.error,
                  ]
                    .filter(Boolean)
                    .join(' — ');
                  return (
                    <div className="orderTagsContainer">
                      <span
                        className={`syncStatusChip ${sync.tone}`}
                        title={tooltip || undefined}
                      >
                        {sync.shortLabel}
                      </span>
                    </div>
                  );
                })()}
              </div>

              {/* Channel Section */}
              <div className="orderSection channelSection">
                <div className="channelLogo">
                  <div className="logoPlaceholder">
                    {order.channel === 'Shopify' && '🛍️'}
                    {order.channel === 'WooCommerce' && '🛒'}
                    {order.channel === 'Amazon' && '📦'}
                    {order.channel === 'Custom' && 'S'}
                  </div>
                  <div className="storeName">Suspire</div>
                </div>
                <div className="vendorName">{order.vendorName}</div>
              </div>

              {/* Product Details Section */}
              <div className="orderSection productDetailsSection">
                <div className="productImageScroll">
                  <button
                    className="scrollBtn scrollLeft"
                    onClick={() => handleImageScroll(order.id, 'left')}
                    aria-label="Previous product image"
                  >
                    ◀
                  </button>
                  <div className="productImageFrame">
                    {imageSrc ? (
                      <img
                        src={imageSrc}
                        alt={displayName}
                        className="productThumbnail"
                      />
                    ) : (
                      <div className="productImagePlaceholder">{imageInitial}</div>
                    )}
                  </div>
                  <button
                    className="scrollBtn scrollRight"
                    onClick={() => handleImageScroll(order.id, 'right')}
                    aria-label="Next product image"
                  >
                    ▶
                  </button>
                </div>
                {indicatorCount > 1 && (
                  <div className="imageIndicators">
                    {(order.productMediaEntries || order.productImages || []).map((_, idx) => (
                      <span
                        key={idx}
                        className={`imageIndicator ${currentIdx === idx ? 'active' : ''}`}
                      ></span>
                    ))}
                  </div>
                )}
                <div className="productInfoRow">
                  <div
                    className="productName"
                    title={displayName}
                    onClick={() => handleOrderClick(order.id)}
                  >
                    {displayName}
                  </div>
                  <div className="productQtyBlock">
                    <div className="productQtyLabel">Quantity</div>
                    <div className="productQtyValue">{displayQty}</div>
                  </div>
                </div>

                {/* Processing Timeline Display - Below Product Quantity */}
                {order.processingTimeline && (
                  <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'flex-start' }}>
                    <span style={{
                      background: '#1976D2',
                      color: '#FFFFFF',
                      border: 'none',
                      fontWeight: '600',
                      fontSize: '13px',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      📦 {order.processingTimeline}
                    </span>
                  </div>
                )}
              </div>

              {/* Payout UI section 
                <div className="payoutInfo">
                  <div className="payoutAmount">{formatCurrency(order.payout)}</div>
                  <div className={`payoutStatus ${getPaymentStatusClass(order.payoutStatus)}`}>
                    {order.payoutStatus}
                  </div>
                </div>
               */}

              {/* Payment Section */}
              <div className="orderSection paymentSection">
                <div className="orderValue">{formatCurrency(order.orderValue)}</div>
                <div className={`paymentMode ${getPaymentModeClass(paymentMode)}`}>
                  {paymentMode}
                </div>
              </div>


              {/* Customer Details Section */}
              <div className="orderSection customerSection">
                <div
                  className="customerName"
                  title={maskIfHidden(order.customerName, order.vendorHideCustomerDetails, userRole === 'admin')}
                >
                  {maskIfHidden(order.customerName, order.vendorHideCustomerDetails, userRole === 'admin')}
                </div>
                <div className="customerEmail" title={maskIfHidden(order.customerEmail, order.vendorHideCustomerDetails, userRole === 'admin')}>
                  {maskIfHidden(order.customerEmail, order.vendorHideCustomerDetails, userRole === 'admin')}
                </div>
                <div className="customerPhone">{maskIfHidden(order.customerPhone, order.vendorHideCustomerDetails, userRole === 'admin')}</div>
              </div>

              {/* Address Section */}
              <div className="orderSection addressSection">
                {(() => {
                  const pickupAddrObj = order?.pickup_address?.address;
                  const shipAddrObj = order?.shipping_address;
                  const pickupLines = pickupAddrObj ? formatAddressLines(pickupAddrObj) : (order.pickupAddress && order.pickupAddress !== 'No Address' ? [order.pickupAddress] : []);
                  
                  const shipAddress = shipAddrObj ? formatAddressLines(shipAddrObj).join(', ') : (order.shipTo || '');
                  const shipLines = [maskAddressIfHidden(shipAddress, order.vendorHideCustomerDetails, userRole === 'admin')];

                  const hasPickupAddress = pickupLines.length > 0 && pickupLines.some(line => line && line !== 'No Address');

                  return (
                    <>
                      <div className="addressBlock">
                        <div className="addressLabel">
                          <span>Pick Up</span>
                          {canEditAddresses && (
                            <button
                              className="editAddressBtn"
                              title="Edit Pickup Address"
                              onClick={() => handlePickupAddressEdit(order)}
                            >
                              ✏️
                            </button>
                          )}
                        </div>
                        {order?.pickup_address?.warehouse_name && (
                          <div className="warehouseName" style={{ fontWeight: '600', fontSize: '13px', color: 'var(--gray-700)', marginBottom: '4px' }}>
                            {order.pickup_address.warehouse_name}
                          </div>
                        )}
                        <div className={`addressValue addressValueMultiline ${order.pickupAddress === 'No Warehouse' ? 'noWarehouse' : ''}`}>
                          {hasPickupAddress ? (
                            pickupLines.filter(Boolean).map((line, idx) => (
                              <div key={idx} className="addressLineCompact">{line}</div>
                            ))
                          ) : (
                            <div className="addressLineCompact">No Address</div>
                          )}
                          {order.pickupAddress === 'No Warehouse' && (
                            <span className="warningIcon">⚠️</span>
                          )}
                        </div>
                      </div>
                      <div className="addressBlock">
                        <div className="addressLabel">
                          <span>Ship To</span>
                          {canEditShipToAddresses && canEditAddresses && (
                            <button
                              className="editAddressBtn"
                              title="Edit Shipping Address"
                              onClick={() => handleShipToAddressEdit(order)}
                            >
                              ✏️
                            </button>
                          )}
                        </div>
                        <div className="addressValue addressValueMultiline">
                          {shipLines.filter(Boolean).map((line, idx) => (
                            <div key={idx} className="addressLineCompact">{line}</div>
                          ))}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Fulfillment Details Section */}
              <div className="orderSection fulfillmentSection">
                <div className="dimensionRowFull">
                  <label style={{ fontSize: '12px', color: 'var(--gray-600)', marginBottom: '4px', display: 'block' }}>Length (cm)</label>
                  <input
                    type="number"
                    value={editableDimensions[order.id]?.length || order.length}
                    className="dimensionInputFull"
                    onChange={(e) => handleDimensionChange(order.id, 'length', e.target.value)}
                    readOnly={isReadOnly || hasLabel || hasManualShipping}
                    style={{ width: '100%', padding: '6px', boxSizing: 'border-box' }}
                  />
                </div>
                <div className="dimensionRowFull">
                  <label style={{ fontSize: '12px', color: 'var(--gray-600)', marginBottom: '4px', display: 'block' }}>Breadth (cm)</label>
                  <input
                    type="number"
                    value={editableDimensions[order.id]?.breadth || order.breadth}
                    className="dimensionInputFull"
                    onChange={(e) => handleDimensionChange(order.id, 'breadth', e.target.value)}
                    readOnly={isReadOnly || hasLabel || hasManualShipping}
                    style={{ width: '100%', padding: '6px', boxSizing: 'border-box' }}
                  />
                </div>
                <div className="dimensionRowFull">
                  <label style={{ fontSize: '12px', color: 'var(--gray-600)', marginBottom: '4px', display: 'block' }}>Height (cm)</label>
                  <input
                    type="number"
                    value={editableDimensions[order.id]?.height || order.height}
                    className="dimensionInputFull"
                    onChange={(e) => handleDimensionChange(order.id, 'height', e.target.value)}
                    readOnly={isReadOnly || hasLabel || hasManualShipping}
                    style={{ width: '100%', padding: '6px', boxSizing: 'border-box' }}
                  />
                </div>
                <div className="dimensionRowFull">
                  <label style={{ fontSize: '12px', color: 'var(--gray-600)', marginBottom: '4px', display: 'block' }}>
                    Volumetric Weight (Grams) - <strong>{volumetricWeightLabel}</strong>
                  </label>
                  <input
                    type="number"
                    value={editableDimensions[order.id]?.weight || order.weight}
                    className="dimensionInputFull"
                    onChange={(e) => handleDimensionChange(order.id, 'weight', e.target.value)}
                    readOnly={isReadOnly || hasLabel || hasManualShipping}
                    style={{ width: '100%', padding: '6px', boxSizing: 'border-box' }}
                  />
                </div>



                {showShipperInfo && (
                  <div className="shipperInfo">
                    {activeTab === 'all' && !hasLabel && !hasManualShipping ? (
                      // For 'all' tab: Show "Label Not Created" only when neither label nor manual shipping exists
                      <div className="deliveryStatusBadge" style={{ background: '#fff8e6', color: '#8a6d00' }}>
                        Label Not Created
                      </div>
                    ) : (
                      // Original display for all tabs (including 'all' tab when label exists)
                      <>
                        <div className="shipperName">{order.partner}</div>
                        {showAWB && order.awbNumber && (
                          <div className="awbNumber">
                            {order.awbNumber}
                            <button className="copyBtn" title="Copy AWB"
                              onClick={() => navigator.clipboard.writeText(order.awbNumber)}
                            >📋</button>
                          </div>
                        )}
                        {showAWB && order.awbNumber && (() => {
                          const editState = trackingLinkEdit[order.id];
                          const link = editState?.value !== undefined
                            ? editState.value
                            : (order.trackingLink || '');
                          if (editState?.isEditing && userRole === 'admin') {
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                                <input
                                  autoFocus
                                  type="text"
                                  value={editState.value}
                                  onChange={e => setTrackingLinkEdit(prev => ({ ...prev, [order.id]: { ...prev[order.id], value: e.target.value } }))}
                                  onKeyDown={e => { if (e.key === 'Enter') handleTrackingLinkSave(order.id, editState.value); if (e.key === 'Escape') handleTrackingLinkCancel(order.id); }}
                                  disabled={editState.saving}
                                  placeholder="e.g. https://..."
                                  style={{ fontSize: '11px', padding: '2px 4px', border: '1px solid #ccc', borderRadius: '3px', width: '120px' }}
                                />
                                <button
                                  onClick={() => handleTrackingLinkSave(order.id, editState.value)}
                                  disabled={editState.saving}
                                  style={{ fontSize: '11px', background: 'none', border: 'none', cursor: 'pointer', color: 'green' }}
                                >{editState.saving ? '…' : '✓'}</button>
                                <button
                                  onClick={() => handleTrackingLinkCancel(order.id)}
                                  disabled={editState.saving}
                                  style={{ fontSize: '11px', background: 'none', border: 'none', cursor: 'pointer', color: 'red' }}
                                >✕</button>
                              </div>
                            );
                          }
                          return (
                            <div style={{ fontSize: '11px', marginTop: '3px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                              {link ? (link.startsWith('http') ? (
                                <a href={link} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-orange)', textDecoration: 'underline' }} title={link}>Tracking Link</a>
                              ) : (
                                <span style={{ color: 'var(--gray-600)' }} title={link}>{link}</span>
                              )) : (
                                <span style={{ color: 'var(--gray-400)' }}>No link</span>
                              )}
                              {!isReadOnly && userRole === 'admin' && (
                                <button
                                  onClick={() => handleTrackingLinkEdit(order.id, link)}
                                  title="Edit tracking link"
                                  style={{ fontSize: '10px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)', padding: '0 2px' }}
                                >✏️</button>
                              )}
                            </div>
                          );
                        })()}
                        {!['delivered', 'all'].includes(activeTab) && order.deliveryDays !== '' && order.deliveryDays !== null && order.deliveryDays !== undefined && (
                          <div className="deliveryDaysBadge" style={{ marginTop: '6px', fontSize: '11px', fontWeight: 'bold', color: '#047857', background: '#d1fae5', padding: '2px 6px', borderRadius: '4px', display: 'inline-block' }}>
                            Delivery Days: {order.deliveryDays}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {!showShipperInfo && showAWB && (orderLabelStatus[order.id]?.awbCode || order.awbNumber) && (
                  <div className="awbNumber">
                    {orderLabelStatus[order.id]?.awbCode || order.awbNumber}
                    <button
                      className="copyBtn"
                      title="Copy AWB"
                      onClick={() => navigator.clipboard.writeText(orderLabelStatus[order.id]?.awbCode || order.awbNumber)}
                    >
                      📋
                    </button>
                  </div>
                )}

                {!showShipperInfo && showAWB && (orderLabelStatus[order.id]?.awbCode || order.awbNumber) && (() => {
                  const editState = trackingLinkEdit[order.id];
                  const link = editState?.value !== undefined ? editState.value : (order.trackingLink || '');
                  if (editState?.isEditing && userRole === 'admin') {
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                        <input
                          autoFocus
                          type="text"
                          value={editState.value}
                          onChange={e => setTrackingLinkEdit(prev => ({ ...prev, [order.id]: { ...prev[order.id], value: e.target.value } }))}
                          onKeyDown={e => { if (e.key === 'Enter') handleTrackingLinkSave(order.id, editState.value); if (e.key === 'Escape') handleTrackingLinkCancel(order.id); }}
                          disabled={editState.saving}
                          placeholder="e.g. https://..."
                          style={{ fontSize: '11px', padding: '2px 4px', border: '1px solid #ccc', borderRadius: '3px', width: '120px' }}
                        />
                        <button
                          onClick={() => handleTrackingLinkSave(order.id, editState.value)}
                          disabled={editState.saving}
                          style={{ fontSize: '11px', background: 'none', border: 'none', cursor: 'pointer', color: 'green' }}
                        >{editState.saving ? '…' : '✓'}</button>
                        <button
                          onClick={() => handleTrackingLinkCancel(order.id)}
                          disabled={editState.saving}
                          style={{ fontSize: '11px', background: 'none', border: 'none', cursor: 'pointer', color: 'red' }}
                        >✕</button>
                      </div>
                    );
                  }
                  return (
                    <div style={{ fontSize: '11px', marginTop: '3px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                      {link ? (link.startsWith('http') ? (
                        <a href={link} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-orange)', textDecoration: 'underline' }} title={link}>Tracking Link</a>
                      ) : (
                        <span style={{ color: 'var(--gray-600)' }} title={link}>{link}</span>
                      )) : (
                        <span style={{ color: 'var(--gray-400)' }}>No link</span>
                      )}
                      {!isReadOnly && userRole === 'admin' && (
                        <button
                          onClick={() => handleTrackingLinkEdit(order.id, link)}
                          title="Edit tracking link"
                          style={{ fontSize: '10px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)', padding: '0 2px' }}
                        >✏️</button>
                      )}
                    </div>
                  );
                })()}

                {!showShipperInfo && activeTab !== 'held' && (
                  <>
                    <div className="shipperSelectionContainer" title={!dimsValid ? "Please fill Length, Breadth, Height and Weight first" : ""}>
                      {activeTab !== 'confirmed' && activeTab !== 'new_orders' ? (
                        /* Fallback dropdown for other tabs (not new_orders or confirmed) */
                        <select
                          className="shipperDropdown"
                          disabled={isReadOnly || hasLabel || !dimsValid}
                          value={isManual[order.id] || hasManualShipping ? MANUAL_CHANNEL_VALUE : (selectedChannels[order.id] || 'none')}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleProzoChannelChange(order.id, value);
                          }}
                          style={!dimsValid ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                        >
                          <option value="none">Select Channel</option>
                          {PROZO_CHANNEL_NAMES.map((ch) => (
                            <option key={ch} value={ch}>
                              {`${ch}-prozo`}
                            </option>
                          ))}
                          <option value={MANUAL_CHANNEL_VALUE}>
                            Manual
                          </option>
                        </select>
                      ) : (
                        /* Clickable box for confirmed and new_orders tabs - opens modal immediately */
                        <div
                          className="shipperDropdownClickable"
                          onClick={() => {
                            if (!isReadOnly && !hasLabel && dimsValid) {
                              setShowShippingCostModal({ isOpen: true, orderId: order.id });
                              // Fetch costs if not already fetched
                              if (!shippingCosts[order.id]?.costs || shippingCosts[order.id]?.costs.length === 0) {
                                handleFetchShippingCosts(order.id);
                              }
                            }
                          }}
                          style={{
                            cursor: (isReadOnly || hasLabel || !dimsValid) ? 'not-allowed' : 'pointer',
                            opacity: (isReadOnly || hasLabel || !dimsValid) ? 0.6 : 1
                          }}
                          title={
                            !dimsValid ? "Please fill Length, Breadth, Height and Weight first" :
                              hasLabel ? "Label already generated" :
                                "Select channel"
                          }
                        >
                          <span className="dropdownText">
                            {isManual[order.id] || hasManualShipping
                              ? 'Manual'
                              : selectedChannels[order.id] && selectedChannels[order.id] !== 'none'
                                ? `${selectedChannels[order.id]}-prozo`
                                : 'Select channel'}
                          </span>
                        </div>
                      )}
                    </div>

                    <button
                      className={`generateLabelBtn ${hasLabel ? 'labelGenerated' :
                        (selectedChannels[order.id] && selectedChannels[order.id] !== 'none') ? 'labelReadyToGenerate' : 'labelNotGenerated'
                        }`}
                      disabled={
                        isReadOnly ||
                        generatingLabel[order.id] ||
                        hasLabel ||
                        ((!selectedChannels[order.id] || selectedChannels[order.id] === 'none') && !isManual[order.id] && !hasManualShipping)
                      }
                      onClick={() => {
                        if (isManual[order.id] || hasManualShipping) {
                          handleOpenManualShipping(order.id);
                        } else {
                          handleGenerateLabel(order.id);
                        }
                      }}
                    >
                      {generatingLabel[order.id]
                        ? '⏳ Generating...'
                        : hasLabel
                          ? '✓ Label Generated'
                          : isManual[order.id] || hasManualShipping
                            ? '✏️ Change Details'
                            : 'Generate Label'
                      }
                    </button>
                  </>
                )}

                {order.errors > 0 && (
                  <div className="errorIndicator">
                    <span className="errorIcon">⚠️</span>
                    {order.errors} Error{order.errors > 1 ? 's' : ''}
                  </div>
                )}
              </div>

              {/* Order Status & Actions Section */}
              <div className="orderSection statusActionsSection">
                <div className={`orderStatusBadge ${getOrderStatusClass(order.orderStatus)}`}>
                  {order.orderStatus}
                </div>
                {/* Penalty indicator — shown on every tab (All Orders, Cancelled, etc.) except the
                    confirmed/admin view, which surfaces it via the interactive Follow Up / Penalty
                    dropdown. Keyed off penaltyPercentage (set on the order when a penalty is applied)
                    so it stays visible regardless of the order's current status or follow-up stage. */}
                {(order.penaltyPercentage > 0 || order.followUpStage === 'penalty') && !(activeTab === 'confirmed' && userRole === 'admin') && (
                  <div
                    style={{
                      marginTop: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#fff',
                      backgroundColor: '#dc3545',
                      padding: '4px 8px',
                      borderRadius: '4px',
                    }}
                    title="A penalty has been applied to this order"
                  >
                    {'⚠️'} Penalty Applied{order.penaltyPercentage > 0 ? ` (${order.penaltyPercentage}%)` : ''}
                  </div>
                )}
                {(activeTab === 'exceptions' || activeTab === 'rto') && order.reasonLabel && (
                  <div className="exceptionReason">
                    <span className="exceptionIcon">⚠️</span>
                    {order.reasonLabel}
                  </div>
                )}
                {hasLabel && (
                  <>
                    {/* Needs BOTH documents. The button stays visually disabled until
                        the manifest lands, but clicking it still explains why — a
                        native `disabled` swallows the click, so we style it disabled
                        (aria-disabled + class) and branch inside the handler. */}
                    <button
                      className={`downloadDocsBtnSmall${docsReady ? '' : ' isDisabled'}`}
                      aria-disabled={!docsReady || downloadingDocuments[order.id]}
                      onClick={async () => {
                        if (downloadingDocuments[order.id]) return;
                        // Manifest not ready: surface a state-specific message rather
                        // than attempting a download that would 404.
                        if (!docsReady) {
                          if (manifestStuck) {
                            showToast({
                              type: 'error',
                              message: 'Manifest is not available for this order yet. Use "Retry manifest" to generate it.',
                            });
                          } else {
                            showToast({
                              type: 'info',
                              message: 'Documents are still being prepared. The manifest usually lands within a minute — this button will enable automatically.',
                            });
                          }
                          return;
                        }
                        try {
                          setDownloadingDocuments(prev => ({ ...prev, [order.id]: true }));
                          const api = await import('../../api/api');
                          const resp = await api.downloadCombinedDocuments(order.id);
                          if (resp && resp.success && resp.combined_url) {
                            const filename = resp.filename
                              || `order_${order.orderNumber || order.id}_combined_documents.pdf`;
                            await api.downloadFileFromUrl(resp.combined_url, filename);
                          } else {
                            showToast({ type: 'error', message: resp?.message || 'Failed to download documents' });
                          }
                        } catch (err) {
                          console.error('Error downloading combined documents', err);
                          showToast({ type: 'error', message: err?.message || 'Failed to download combined documents' });
                        } finally {
                          setDownloadingDocuments(prev => ({ ...prev, [order.id]: false }));
                        }
                      }}
                      title={docsReady
                        ? 'Download Documents'
                        : manifestStuck
                          ? 'Manifest is not available yet — use "Retry manifest" to generate it.'
                          : 'Manifest is still being generated. This usually takes under a minute.'}
                    >
                      {downloadingDocuments[order.id] ? (
                        <>
                          <span className="downloadSpinner"></span>
                          Downloading...
                        </>
                      ) : docsReady ? (
                        'Download Manifest & Label'
                      ) : manifestStuck ? (
                        'Documents unavailable'
                      ) : (
                        'Preparing documents…'
                      )}
                    </button>

                    {/* Escape hatch: Prozo only retries the manifest twice after label
                        generation, so it can stay missing indefinitely.
                        Offered only on the active leg — generateOrderManifest is
                        order-scoped and would retry the wrong shipment while an
                        earlier one is on display. */}
                    {!hasManifest && (
                      <button
                        className="downloadDocsBtnSmall"
                        disabled={retryingManifest[order.id]}
                        onClick={async () => {
                          try {
                            setRetryingManifest(prev => ({ ...prev, [order.id]: true }));
                            const api = await import('../../api/api');
                            const resp = await api.generateOrderManifest(order.id);
                            if (resp && resp.success && resp.manifest_url) {
                              showToast({ type: 'success', message: 'Manifest ready. You can download the documents now.' });
                              onOrderAction?.('refresh_orders', order.id, { source: 'manifest_retry' });
                            } else {
                              showToast({ type: 'error', message: resp?.message || 'Manifest is not ready yet. Please try again shortly.' });
                            }
                          } catch (err) {
                            console.error('Error retrying manifest generation', err);
                            showToast({ type: 'error', message: 'Could not generate the manifest. Please try again shortly.' });
                          } finally {
                            setRetryingManifest(prev => ({ ...prev, [order.id]: false }));
                          }
                        }}
                        title="Ask Prozo for the manifest again"
                      >
                        {retryingManifest[order.id] ? 'Retrying…' : 'Retry manifest'}
                      </button>
                    )}
                  </>
                )}

                {order.requiresPrescription && (
                  <button
                    className={`downloadDocsBtnSmall ${order.prescription?.uploaded ? 'prescriptionUploadedBtn' : 'prescriptionRequiredBtn'}`}
                    disabled={isReadOnly && !order.prescription?.uploaded}
                    onClick={() => openPrescriptionModal(order.id)}
                    title={
                      order.prescription?.uploaded
                        ? `Prescription: ${order.prescription.kind === 'link' ? `link (${order.prescription.link_host})` : order.prescription.original_filename}`
                        : 'A prescription (PDF or link) is required before a label can be generated'
                    }
                    style={{ marginTop: '8px' }}
                  >
                    {order.prescription?.uploaded ? 'Prescription ✓' : 'Add Prescription'}
                  </button>
                )}
                {isAdmin && order.prescription?.uploaded && (
                  <button
                    className="downloadDocsBtnSmall"
                    onClick={() => openOrderPrescription(order.id)}
                    title={order.prescription.kind === 'link' ? 'Open the prescription link' : 'Download the uploaded prescription'}
                    style={{ marginTop: '8px' }}
                  >
                    {order.prescription.kind === 'link' ? 'Open Prescription' : 'Download Prescription'}
                  </button>
                )}
                {(hasLabel || order.shipment?.manifest_url || hasManualShipping) && (
                  <>
                    {/* Invoice actions */}
                    {!hasInvoice && (
                      <button
                        className="downloadDocsBtnSmall invoiceGenerateBtnSmall"
                        disabled={isReadOnly || (!hasLabel && !hasManualShipping) || generatingInvoice[order.id]}
                        onClick={() => handleGenerateInvoice(order.id)}
                        title={
                          (!hasLabel && !hasManualShipping)
                            ? 'Generate a shipping label or add manual shipping details before creating an invoice.'
                            : generatingInvoice[order.id]
                              ? 'Generating invoice…'
                              : 'Generate customer invoice'
                        }
                        style={{
                          marginTop: '8px',
                          opacity: (isReadOnly || (!hasLabel && !hasManualShipping) || generatingInvoice[order.id]) ? 0.6 : 1,
                          cursor: (isReadOnly || (!hasLabel && !hasManualShipping)) ? 'not-allowed' : (generatingInvoice[order.id] ? 'wait' : 'pointer'),
                        }}
                      >
                        {generatingInvoice[order.id] ? (
                          <>
                            <span className="downloadSpinner"></span>
                            Generating…
                          </>
                        ) : (
                          'Generate Invoice'
                        )}
                      </button>
                    )}

                    {hasInvoice && (
                      <div className="statusButtonDropdown invoiceDropdownWrapper">
                        <button
                          className="statusDropdownButton"
                          onClick={() =>
                            setShowInvoiceDropdown(prev => ({ ...prev, [order.id]: !prev[order.id] }))
                          }
                          disabled={
                            isReadOnly ||
                            generatingInvoice[order.id] ||
                            downloadingInvoice[order.id] ||
                            cancellingInvoice[order.id]
                          }
                        >
                          {generatingInvoice[order.id] && 'Invoice: Generating…'}
                          {downloadingInvoice[order.id] && 'Invoice: Downloading…'}
                          {cancellingInvoice[order.id] && 'Invoice: Cancelling…'}
                          {!generatingInvoice[order.id] &&
                            !downloadingInvoice[order.id] &&
                            !cancellingInvoice[order.id] &&
                            'Invoice ▾'}
                        </button>
                        {showInvoiceDropdown[order.id] &&
                          !generatingInvoice[order.id] &&
                          !downloadingInvoice[order.id] &&
                          !cancellingInvoice[order.id] && (
                            <>
                              <div
                                className="dropdownOverlay"
                                onClick={() =>
                                  setShowInvoiceDropdown(prev => ({ ...prev, [order.id]: false }))
                                }
                              ></div>
                              <div className="statusDropdownMenu">
                                <button
                                  onClick={() => {
                                    setShowInvoiceDropdown(prev => ({ ...prev, [order.id]: false }));
                                    handleDownloadInvoice(order.id);
                                  }}
                                >
                                  Download Invoice
                                </button>
                                <button
                                  onClick={() => {
                                    setShowInvoiceDropdown(prev => ({ ...prev, [order.id]: false }));
                                    handleCancelInvoice(order.id);
                                  }}
                                  disabled={cancellingInvoice[order.id]}
                                >
                                  Cancel Invoice
                                </button>
                              </div>
                            </>
                          )}
                      </div>
                    )}
                  </>
                )}
                <div className="statusActions">
                  {/* The only cancelled-bucket action is the admin-only "Move to New Orders", so hide the dropdown for non-admins. */}
                  {!(activeTab === 'cancelled' && !isAdmin) && (
                  <div className="statusButtonDropdown">
                    <button
                      className="statusDropdownButton"
                      onClick={() => toggleStatusDropdown(order.id)}
                      disabled={isReadOnly}
                    >
                      {activeTab === 'new_orders' && (canConfirmOrders ? 'Confirm Order ▾' : 'Order Actions ▾')}
                      {activeTab === 'confirmed' && 'Ready to Ship ▾'}
                      {activeTab === 'ready_to_ship' && 'In Transit ▾'}
                      {activeTab === 'in_transit' && 'Delivered ▾'}
                      {activeTab === 'exceptions' && 'Delivered ▾'}
                      {activeTab === 'delivered' && 'In Transit ▾'}
                      {activeTab === 'held' && (isAdmin ? 'Move to New Orders ▾' : 'Change Status ▾')}
                      {activeTab === 'rto' && (isAdmin ? 'Move to New Orders ▾' : 'Change Status ▾')}
                      {activeTab === 'lost' && 'Change Status ▾'}
                      {activeTab === 'cancelled' && 'Move to New Orders ▾'}
                      {activeTab === 'damaged' && 'Change Status ▾'}
                      {activeTab === 'all' && 'Change Status ▾'}
                    </button>
                    {showStatusDropdown[order.id] && (
                      <>
                        <div className="dropdownOverlay" onClick={() => toggleStatusDropdown(order.id)}></div>
                        <div className="statusDropdownMenu">
                          {activeTab === 'new_orders' && (
                            <>
                              {canConfirmOrders && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('confirm_order', order.id); }}>Confirm Order</button>
                              )}
                              <button onClick={() => { toggleStatusDropdown(order.id); if (!canMarkReadyToShip) { blockReadyToShip(); } else { onOrderAction('ready_to_ship', order.id); } }}>Ready to Ship</button>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('in_transit', order.id); }}>In Transit</button>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('delivered', order.id); }}>Delivered</button>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('rto', order.id); }}>RTO</button>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('exceptions', order.id); }}>Exception</button>
                              {isAdmin && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('hold', order.id); }}>Hold</button>
                              )}
                              {canCancelOrders && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('cancel', order.id); }}>Cancel</button>
                              )}
                            </>
                          )}
                          {activeTab === 'confirmed' && (
                            <>
                              <button
                                onClick={() => {
                                  toggleStatusDropdown(order.id);
                                  if (!canMarkReadyToShip) {
                                    blockReadyToShip();
                                  } else {
                                    onOrderAction('ready_to_ship', order.id);
                                  }
                                }}
                                title={!canMarkReadyToShip ? 'Generate label or add manual shipping first' : ''}
                              >
                                Ready to Ship
                              </button>
                              {isAdmin && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('hold', order.id); }}>Hold</button>
                              )}
                              {canCancelOrders && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('cancel', order.id); }}>Cancel</button>
                              )}
                            </>
                          )}
                          {activeTab === 'ready_to_ship' && (
                            <>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('in_transit', order.id); }}>In Transit</button>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('delivered', order.id); }}>Delivered</button>
                              {isAdmin && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('hold', order.id); }}>Hold</button>
                              )}
                              {canCancelOrders && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('cancel', order.id); }}>Cancel</button>
                              )}
                            </>
                          )}
                          {activeTab === 'in_transit' && (
                            <>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('delivered', order.id); }}>Delivered</button>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('exceptions', order.id); }}>Exception</button>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('rto', order.id); }}>RTO</button>
                              {isAdmin && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('hold', order.id); }}>Hold</button>
                              )}
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('lost', order.id); }}>Lost</button>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('damaged', order.id); }}>Damaged</button>
                              {canCancelOrders && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('cancel', order.id); }}>Cancel</button>
                              )}
                            </>
                          )}
                          {activeTab === 'exceptions' && (
                            <>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('delivered', order.id); }}>Delivered</button>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('rto', order.id); }}>RTO</button>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('retry', order.id); }}>Retry Delivery</button>
                              {isAdmin && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('hold', order.id); }}>Hold</button>
                              )}
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('lost', order.id); }}>Lost</button>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('damaged', order.id); }}>Damaged</button>
                              {canCancelOrders && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('cancel', order.id); }}>Cancel</button>
                              )}
                            </>
                          )}
                          {activeTab === 'delivered' && (
                            <>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('in_transit', order.id); }}>In Transit</button>
                              {canConfirmOrders && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('confirmed', order.id); }}>Confirmed</button>
                              )}
                              {isAdmin && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('hold', order.id); }}>Hold</button>
                              )}
                              {canCancelOrders && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('cancel', order.id); }}>Cancel</button>
                              )}
                            </>
                          )}
                          {activeTab === 'held' && (
                            <>
                              {isAdmin && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('new_order', order.id); }}>Move to New Orders</button>
                              )}
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('confirmed', order.id); }}>Move to Confirmed Order</button>
                              {canConfirmOrders && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('ready_to_ship', order.id); }}>Ready to Ship</button>
                              )}
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('in_transit', order.id); }}>In Transit</button>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('delivered', order.id); }}>Delivered</button>
                              {canCancelOrders && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('cancel', order.id); }}>Cancel Order</button>
                              )}
                            </>
                          )}
                          {activeTab === 'rto' && (
                            <>
                              {isAdmin && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('new_order', order.id); }}>Move to New Orders</button>
                              )}
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('lost', order.id); }}>Lost</button>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('damaged', order.id); }}>Damaged</button>
                              {canCancelOrders && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('cancel', order.id); }}>Cancel Order</button>
                              )}
                            </>
                          )}
                          {activeTab === 'lost' && (
                            <>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('rto', order.id); }}>RTO</button>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('damaged', order.id); }}>Damaged</button>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('in_transit', order.id); }}>In Transit</button>
                            </>
                          )}
                          {activeTab === 'damaged' && (
                            <>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('rto', order.id); }}>RTO</button>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('lost', order.id); }}>Lost</button>
                            </>
                          )}
                          {activeTab === 'cancelled' && (
                            <>
                              {isAdmin && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('new_order', order.id); }}>Move to New Orders</button>
                              )}
                              {canCancelOrders && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('cancel', order.id); }}>Cancel Order</button>
                              )}
                            </>
                          )}
                          {activeTab === 'all' && (
                            <>
                              {canConfirmOrders && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('confirmed', order.id); }}>Confirmed</button>
                              )}
                              <button onClick={() => { toggleStatusDropdown(order.id); if (!canMarkReadyToShip) { blockReadyToShip(); } else { onOrderAction('ready_to_ship', order.id); } }}>Ready to Ship</button>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('in_transit', order.id); }}>In Transit</button>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('delivered', order.id); }}>Delivered</button>
                              {isAdmin && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('hold', order.id); }}>Hold</button>
                              )}
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('lost', order.id); }}>Lost</button>
                              <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('damaged', order.id); }}>Damaged</button>
                              {canCancelOrders && (
                                <button onClick={() => { toggleStatusDropdown(order.id); onOrderAction('cancel', order.id); }}>Cancel</button>
                              )}
                            </>
                          )}
                        </div>
                      </>
                    )}

                  </div>
                  )}
                  {/* Follow Up / Penalty dropdown — confirmed tab, admin only */}
                  {activeTab === 'confirmed' && userRole === 'admin' && (
                    <div className="statusButtonDropdown" style={{ marginTop: '4px' }}>
                      <button
                        className="statusDropdownButton"
                        onClick={() => toggleFollowUpDropdown(order.id)}
                        disabled={isReadOnly}
                        style={{
                          fontSize: '11px',
                          padding: '4px 8px',
                          backgroundColor: order.followUpStage === 'penalty' ? '#dc3545' : order.exceptionActive ? '#ffc107' : (order.followUpStage && order.followUpStage !== 'none') ? '#17a2b8' : '#6c757d',
                          color: order.exceptionActive ? '#333' : '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          width: '100%',
                        }}
                      >
                        {order.followUpStage === 'penalty' ? '⚠️ Penalty Applied' : order.exceptionActive ? '⛔ Exception Active' : (order.followUpStage && order.followUpStage !== 'none') ? `📩 ${getFollowUpStageLabel(order.followUpStage)}` : 'Follow Up ▾'}
                      </button>
                      {showFollowUpDropdown[order.id] && (
                        <>
                          <div className="dropdownOverlay" onClick={() => toggleFollowUpDropdown(order.id)}></div>
                          <div className="statusDropdownMenu">
                            {(() => {
                              const fuStage = order.followUpStage || 'none';
                              const firstDone = ['first_follow_up', 'second_follow_up', 'penalty'].includes(fuStage);
                              const secondDone = ['second_follow_up', 'penalty'].includes(fuStage);
                              const exActive = order.exceptionActive;
                              const penApplied = order.followUpStage === 'penalty';
                              return (
                                <>
                                  <button
                                    disabled={exActive || penApplied || firstDone}
                                    onClick={() => !(exActive || penApplied || firstDone) && openFollowUpModal(order.id, 'first_follow_up')}
                                    style={{ opacity: (firstDone || exActive || penApplied) ? 0.5 : 1, cursor: (firstDone || exActive || penApplied) ? 'default' : 'pointer', textDecoration: (firstDone || exActive || penApplied) ? 'line-through' : 'none' }}
                                    title={firstDone ? 'Already sent' : exActive ? 'Blocked by active exception' : penApplied ? 'Penalty already applied' : 'Send first follow-up'}
                                  >
                                    {firstDone ? '✅ First Follow Up' : '📩 First Follow Up'}
                                  </button>
                                  <button
                                    disabled={exActive || penApplied || secondDone}
                                    onClick={() => !(exActive || penApplied || secondDone) && openFollowUpModal(order.id, 'second_follow_up')}
                                    style={{ opacity: (secondDone || exActive || penApplied) ? 0.5 : 1, cursor: (secondDone || exActive || penApplied) ? 'default' : 'pointer', textDecoration: (secondDone || exActive || penApplied) ? 'line-through' : 'none' }}
                                    title={secondDone ? 'Already sent' : exActive ? 'Blocked by active exception' : penApplied ? 'Penalty already applied' : 'Send second follow-up'}
                                  >
                                    {secondDone ? '✅ Second Follow Up' : '📩 Second Follow Up'}
                                  </button>
                                  <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #eee' }} />
                                  <button
                                    disabled={penApplied && !exActive}
                                    style={{ color: '#856404', position: 'relative', opacity: (penApplied && !exActive) ? 0.5 : 1, textDecoration: (penApplied && !exActive) ? 'line-through' : 'none', cursor: (penApplied && !exActive) ? 'default' : 'pointer' }}
                                    onClick={() => !(penApplied && !exActive) && openFollowUpModal(order.id, 'exception')}
                                    title={exActive && order.exceptionNote ? `Note: ${order.exceptionNote}` : penApplied && !exActive ? 'Cannot grant exception after penalty is applied' : ''}
                                  >
                                    {exActive ? '🔴 Revoke Exception' : '⛔ Grant Exception'}
                                    {exActive && order.exceptionNote && (
                                      <span style={{ marginLeft: '4px', fontSize: '10px', color: '#888', fontStyle: 'italic' }}>ℹ️</span>
                                    )}
                                  </button>
                                  <button
                                    disabled={exActive || penApplied}
                                    style={{ color: '#dc3545', opacity: (exActive || penApplied) ? 0.5 : 1, textDecoration: (penApplied || exActive) ? 'line-through' : 'none', cursor: (exActive || penApplied) ? 'default' : 'pointer' }}
                                    onClick={() => !(exActive || penApplied) && openFollowUpModal(order.id, 'penalty')}
                                    title={penApplied ? 'Penalty already applied' : exActive ? 'Cannot apply penalty while exception is active' : 'Apply penalty'}
                                  >
                                    {penApplied ? '\u2705 Penalty Applied' : '\u26a0\ufe0f Apply Penalty'}
                                  </button>
                                </>
                              );
                            })()}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  <div className="actionButtons">
                    <br></br>
                    <div className="emailButtonDropdown">
                      <button
                        className="emailDropdownButton"
                        onClick={() => toggleEmailDropdown(order.id)}
                        disabled={isReadOnly}
                      >
                        Email ▾
                      </button>
                      {showEmailDropdown[order.id] && (
                        <>
                          <div className="dropdownOverlay" onClick={() => toggleEmailDropdown(order.id)}></div>
                          <div className="emailDropdownMenu">
                            <button onClick={() => { toggleEmailDropdown(order.id); onOrderAction('email_vendor', order.id); }}>
                              Email Order to Vendor
                            </button>
                            <button onClick={() => { toggleEmailDropdown(order.id); onOrderAction('email_invoice', order.id); }}>
                              Email Invoice to Customer
                            </button>
                            {activeTab === 'new_orders' && userRole === 'admin' && (
                              <button
                                onClick={() => {
                                  toggleEmailDropdown(order.id);
                                  setAddressVerifEmailSent(prev => ({ ...prev, [order.id]: true }));
                                  onOrderAction('email_address_verification', order.id);
                                }}
                                style={{
                                  color: addressVerifEmailSent[order.id] ? '#fff' : undefined,
                                  backgroundColor: addressVerifEmailSent[order.id] ? '#28a745' : undefined,
                                  fontWeight: addressVerifEmailSent[order.id] ? '600' : undefined,
                                }}
                                title="Send address verification request to customer"
                              >
                                {addressVerifEmailSent[order.id] ? 'Address Mail Sent' : 'Request Address'}
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Shipping Address Modal */}
      <ShippingAddressModal
        isOpen={shipToModal.isOpen}
        onClose={closeShipToModal}
        order={shipToOrder}
        onOrderAction={onOrderAction}
        isReadOnly={isReadOnly || !canEditShipToAddresses}
      />

      {/* Pickup Address Modal */}
      <PickupAddressModal
        isOpen={pickupModal.isOpen}
        onClose={closePickupModal}
        order={pickupOrder}
        onOrderAction={onOrderAction}
      />

      {/* Shipping Cost Modal */}
      <ShippingCostModal
        isOpen={showShippingCostModal.isOpen}
        onClose={closeShippingCostModal}
        orderId={showShippingCostModal.orderId}
      />

      {/* Manual Shipping Details Modal */}
      <ManualShippingDetailsModal
        isOpen={manualShippingModal.isOpen}
        onClose={closeManualShippingModal}
        order={manualShippingOrder}
        onOrderAction={onOrderAction}
        editableDimensions={editableDimensions}
      />

      {/* Prescription Upload Modal */}
      {prescriptionModal.isOpen && prescriptionOrder && (
        <PrescriptionUploadModal
          order={prescriptionOrder}
          mode={prescriptionModal.mode}
          // Admins can always upload. Vendors can make the first upload any time,
          // but cannot replace it once the order has a label or AWB (mirrors the server).
          canUpload={
            !isReadOnly &&
            (isAdmin ||
              !prescriptionOrder.prescription?.uploaded ||
              !(prescriptionOrder.shipment?.label_url || prescriptionOrder.shipment?.awb_number))
          }
          onClose={closePrescriptionModal}
          onSaved={(prescription) => {
            if (onOrderAction) {
              onOrderAction('refresh_orders', prescriptionOrder.id, {
                source: 'prescription_update',
                prescription,
              });
            }
          }}
        />
      )}

      {/* Tags Popup Modal */}
      {tagsPopup.visible && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            borderRadius: '16px',
            boxShadow: '0 12px 48px rgba(0,0,0,0.2)',
            padding: '24px',
            zIndex: 10000,
            minWidth: '350px',
            maxWidth: '550px',
            maxHeight: '85vh',
            overflowY: 'auto'
          }}
        >
          {/* Close Button */}
          <button
            onClick={closeTagsPopup}
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              background: 'transparent',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '0',
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
              e.currentTarget.style.color = '#333';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#666';
            }}
          >
            ×
          </button>

          {/* Header */}
          <div style={{
            fontWeight: 'bold',
            marginBottom: '20px',
            fontSize: '18px',
            color: '#00bfae',
            paddingRight: '28px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            📦 Order Tags
          </div>

          {/* Tags Grid */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {tagsPopup.tags.map((tag, index) => (
              <span
                key={index}
                className="orderTagBadge"
                style={{
                  background: '#F76B1C',
                  color: 'white',
                  padding: '10px 18px',
                  borderRadius: '25px',
                  fontSize: '14px',
                  fontWeight: '700',
                  boxShadow: '0 4px 12px rgba(247, 107, 28, 0.4)',
                  border: '2px solid #e55a0f',
                  transition: 'all 0.3s ease',
                  cursor: 'default'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.background = '#e55a0f';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(247, 107, 28, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.background = '#F76B1C';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(247, 107, 28, 0.4)';
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
    </div >

      {/* Follow-Up / Penalty Modal */}
      {followUpModal.isOpen && (
        <div className="modalOverlay" onClick={closeFollowUpModal}>
          <div className="shippingCostModal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modalHeader">
              <div>
                <h3>{getFollowUpModalTitle(followUpModal.action)}</h3>
                <div className="orderInfoSubtext">Order# {(() => { const o = orders.find(o => o.id === followUpModal.orderId); return o?.order_number || o?.orderNumber || followUpModal.orderId; })()}</div>
              </div>
              <button className="closeModalBtn" onClick={closeFollowUpModal} aria-label="Close modal">×</button>
            </div>
            <div className="modalBody" style={{ padding: '16px' }}>
              <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#555', lineHeight: '1.5' }}>
                {getFollowUpModalDescription(followUpModal.action, orders.find(o => o.id === followUpModal.orderId))}
              </p>
              {followUpModal.action === 'penalty' && (
                <div style={{ padding: '10px', backgroundColor: '#fff3cd', borderRadius: '6px', marginBottom: '12px', border: '1px solid #ffc107' }}>
                  <strong style={{ color: '#856404' }}>⚠️ Warning:</strong>
                  <span style={{ color: '#856404', marginLeft: '4px', fontSize: '13px' }}>A penalty of 10% of monthly payout will be imposed on this brand. This action will be recorded and the brand will be notified via email.</span>
                </div>
              )}
              {(() => {
                const modalOrder = orders.find(o => o.id === followUpModal.orderId);
                const isRevoke = followUpModal.action === 'exception' && modalOrder?.exceptionActive;
                const isGrant = followUpModal.action === 'exception' && !modalOrder?.exceptionActive;
                if (isRevoke) {
                  // Revoke: show the existing note read-only for reference, no textarea
                  return modalOrder?.exceptionNote ? (
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#333' }}>Exception Note</label>
                      <div style={{ padding: '8px', fontSize: '13px', backgroundColor: '#f8f9fa', border: '1px solid #ddd', borderRadius: '6px', color: '#555', whiteSpace: 'pre-wrap' }}>
                        {modalOrder.exceptionNote}
                      </div>
                    </div>
                  ) : null;
                }
                if (isGrant) {
                  // Grant exception: show notes textarea
                  return (
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600', color: '#333' }}>Exception Note (optional)</label>
                      <textarea
                        value={followUpNotes}
                        onChange={(e) => setFollowUpNotes(e.target.value)}
                        placeholder="Reason for granting exception..."
                        rows={3}
                        style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', resize: 'vertical', fontFamily: 'inherit' }}
                      />
                    </div>
                  );
                }
                // first/second follow-up and penalty: no notes textarea
                return null;
              })()}
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button onClick={closeFollowUpModal} disabled={followUpLoading} style={{ padding: '8px 16px', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#fff', cursor: 'pointer' }}>Cancel</button>
                <button
                  onClick={handleFollowUpSubmit}
                  disabled={followUpLoading}
                  style={{ padding: '8px 16px', fontSize: '13px', border: 'none', borderRadius: '6px', backgroundColor: followUpModal.action === 'penalty' ? '#dc3545' : '#007bff', color: '#fff', cursor: followUpLoading ? 'not-allowed' : 'pointer', opacity: followUpLoading ? 0.7 : 1 }}
                >
                  {followUpLoading ? '⏳ Processing...' : followUpModal.action === 'penalty' ? 'Confirm Penalty' : followUpModal.action === 'exception' ? (orders.find(o => o.id === followUpModal.orderId)?.exceptionActive ? 'Revoke Exception' : 'Grant Exception') : 'Send Follow-Up'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </>
  );
}

export default OrderListTable;
